import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useData } from "../contexts/DataContext";
import { useAuth } from "../contexts/AuthContext";
import { SigningQuestionsEditor } from "./QuoteTemplates";
import DatePickerField from "../components/DatePickerField";
import { Header } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import axios from "axios";
import { trackEmailDelivery } from "../lib/emailDelivery";
import {
  ArrowLeft, Save, Loader2, Send, Plus, Trash2, FileText, Image, Type,
  DollarSign, PenTool, GripVertical, Eye, Copy, Calculator, Download,
  Mail, Check, Settings2, Percent, Link, Columns, Layers, Smartphone, Monitor, Tablet, X,
  ChevronUp, ChevronDown, FlaskConical, RefreshCw, Workflow as WorkflowIcon, Pause, ShieldCheck,
  RotateCcw, AlertTriangle
} from "lucide-react";
import { saveAs } from "file-saver";
import { CoverPageEditor, TextPageEditor, CustomPageEditor, StructurePageEditor, OptionPageEditor, AddonPageEditor, FinancingPageEditor } from "../components/quote-builder/PageEditors";
import { PagePreviewRenderer } from "../components/quote-templates/PagePreviewRenderer";
import { CoverPageDisplay } from "../components/quote-builder/CoverPageDisplay";
import { RichTextContent } from "../components/ui/RichTextEditor";
import AutosaveStatus from "../components/AutosaveStatus";
import { useAutosave } from "../hooks/useAutosave";
import { resolveImageUrl } from "../components/quote-templates/ContentBlocks";
import { BeforeAfterDisplay } from "../components/quote-templates/BeforeAfterDisplay";
import { PricingTab } from "../components/quote-builder/PricingTab";
import { SectionErrorBoundary } from "../components/SectionErrorBoundary";
import { applyMargin, applyProfit } from "../utils/pricing";
import { computeQuoteDiscounts } from "../lib/quoteDiscounts";
import { isQuoteExpired } from "../lib/quoteExpiry";
import { VariableInsertButton } from "../components/ui/VariableInsertButton";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const PAGE_TYPES = [
  { value: "cover", label: "Cover Page", icon: FileText, description: "Auto-populates customer info" },
  { value: "text", label: "Text Page", icon: Type, description: "Warranty, terms, FAQs" },
  { value: "custom", label: "Custom Page", icon: Image, description: "Images, text, HTML, tables" },
  { value: "option", label: "Option Page", icon: DollarSign, description: "A priced package (e.g., Silver, Gold, Platinum)" },
  { value: "addon", label: "Add-ons Page", icon: Plus, description: "Optional upgrades customer can toggle" },
  { value: "structure", label: "Structure Page", icon: Layers, description: "Group options (pick one per structure)" },
  { value: "signature", label: "Signature Page", icon: PenTool, description: "Dual signature approval" },
];


// =================== EMAIL ENGAGEMENT BADGE ===================

function fmtRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function fmtRelativeFuture(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const diffMs = then - Date.now();
  if (diffMs <= 0) return "any moment now";
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "in <1m";
  if (mins < 60) return `in ${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `in ${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `in ${days}d`;
  return new Date(iso).toLocaleDateString();
}

function FollowupBadge({ data, isOpen, onOpenChange, onStop }) {
  const exec = data?.executions?.[0];
  if (!exec) return null;
  const idx = exec.current_step_index || 0;
  const total = exec.total_steps || 0;
  const progressPct = total > 0 ? Math.min(100, Math.round(((idx + 1) / total) * 100)) : 0;
  const stepTypeIcon = {
    send_email: "✉",
    send_sms: "💬",
    create_task: "✅",
    condition: "❓",
    move_stage: "→",
    delay: "⏳",
  }[exec.next_outbound_type || ""] || "→";
  const nextLabel = exec.next_outbound_label || exec.current_step_label || "Next step";

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 h-12 px-3 rounded-md border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 text-xs font-semibold whitespace-nowrap"
          data-testid="followup-badge"
          title="Click to view automated follow-up sequence"
        >
          <WorkflowIcon className="w-3.5 h-3.5" />
          <span>Step {idx + 1}/{total}</span>
          {exec.next_run_at && (
            <span className="text-[10px] font-normal opacity-80">{fmtRelativeFuture(exec.next_run_at)}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="px-4 py-3 border-b bg-indigo-50/40">
          <p className="text-[10px] uppercase tracking-widest font-bold text-indigo-700">Automated Follow-Up</p>
          <p className="text-sm font-semibold text-slate-800 mt-0.5">{exec.workflow_name}</p>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div>
            <div className="flex justify-between text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
              <span>Progress</span>
              <span>{progressPct}%</span>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500" style={{ width: `${progressPct}%` }} />
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Step {idx + 1} of {total}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Next outbound</p>
            <p className="text-sm font-medium text-slate-800">
              <span className="mr-1.5">{stepTypeIcon}</span>{nextLabel}
            </p>
            {exec.next_outbound_subject && (
              <p className="text-xs text-slate-500 mt-0.5 italic truncate">"{exec.next_outbound_subject}"</p>
            )}
            {exec.next_run_at && (
              <p className="text-xs text-slate-600 mt-1">
                Fires {fmtRelativeFuture(exec.next_run_at)} · {new Date(exec.next_run_at).toLocaleString()}
              </p>
            )}
          </div>
          <div className="pt-2 border-t flex gap-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1 h-8 text-xs border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => onStop(exec.execution_id)}
              data-testid="stop-followup-btn"
            >
              <Pause className="w-3 h-3 mr-1" />Stop sequence
            </Button>
          </div>
          <p className="text-[10px] text-slate-400 italic">Use Stop when you've closed the deal or had a direct conversation — no more emails will go out.</p>
        </div>
      </PopoverContent>
    </Popover>
  );
}


// =================== EMAIL ENGAGEMENT BADGE ===================

function EmailEngagementBadge({ data }) {
  if (!data || !data.sends || data.sends === 0 || !data.latest) return null;
  const l = data.latest;
  let label = "Sent";
  let palette = "border-slate-300 text-slate-600 bg-slate-50";
  let icon = "✉";
  let timestamp = l.created_at;
  if (l.bounced_at || (l.status || "").toLowerCase() === "bounced") {
    label = "Bounced"; palette = "border-red-300 text-red-700 bg-red-50"; icon = "⚠"; timestamp = l.bounced_at || l.created_at;
  } else if (l.clicked_at) {
    label = `Clicked${l.click_count > 1 ? ` ${l.click_count}×` : ""}`;
    palette = "border-emerald-300 text-emerald-700 bg-emerald-50"; icon = "🖱"; timestamp = l.clicked_at;
  } else if (l.opened_at) {
    label = `Opened${l.open_count > 1 ? ` ${l.open_count}×` : ""}`;
    palette = "border-blue-300 text-blue-700 bg-blue-50"; icon = "👁"; timestamp = l.opened_at;
  } else if (l.delivered_at) {
    label = "Delivered"; palette = "border-sky-300 text-sky-700 bg-sky-50"; icon = "✓"; timestamp = l.delivered_at;
  }
  const tip = `Latest send to ${l.to || "customer"}\nStatus: ${label}\n${l.subject || ""}\nLast event: ${new Date(timestamp).toLocaleString()}`;
  return (
    <div
      className={`inline-flex items-center gap-1.5 h-12 px-3 rounded-md border text-xs font-semibold whitespace-nowrap ${palette}`}
      title={tip}
      data-testid="email-engagement-badge"
    >
      <span className="text-sm leading-none">{icon}</span>
      <span>{label}</span>
      <span className="text-[10px] font-normal opacity-70">{fmtRelative(timestamp)}</span>
      {data.sends > 1 && <span className="ml-1 px-1.5 py-0.5 rounded bg-white/60 text-[9px]">{data.sends} sends</span>}
    </div>
  );
}


// =================== MAIN QUOTE BUILDER ===================

export default function QuoteBuilder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !id || id === "new";
  const fromProject = searchParams.get("from") === "project";
  const fromProjectId = searchParams.get("projectId") || "";
  const backPath = fromProject && fromProjectId ? `/projects/${fromProjectId}` : "/quotes";

  const {
    contacts, fetchContacts, projects, fetchProjects,
    priceList, fetchPriceList, quoteTemplates, fetchQuoteTemplates,
    quoteSettings, fetchQuoteSettings, createQuote, updateQuote,
    updateQuoteStatus, signQuote, calculateQuote
  } = useData();
  const { user } = useAuth();
  const [sendingTest, setSendingTest] = useState(false);
  const [spamScore, setSpamScore] = useState(null);
  const [checkingSpam, setCheckingSpam] = useState(false);
  const [emailEngagement, setEmailEngagement] = useState(null);
  const [followupStatus, setFollowupStatus] = useState(null);
  const [followupPopoverOpen, setFollowupPopoverOpen] = useState(false);
  const [unsigning, setUnsigning] = useState(false);
  const [showUnsignConfirm, setShowUnsignConfirm] = useState(false);

  const [quote, setQuote] = useState({
    project_id: "", contact_id: "", template_id: "",
    status: "draft", pages: [], line_items: [],
    subtotal: 0, markup_amount: 0, tax_amount: 0, total: 0,
    notes: "", valid_until: "",
    pricing_mode: "margin",
    customer_signature: {}, admin_signature: {}
  });

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("details");
  const [showPageModal, setShowPageModal] = useState(false);
  const [showLineItemModal, setShowLineItemModal] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showCustomerPreview, setShowCustomerPreview] = useState(false);
  const [showLivePreview, setShowLivePreview] = useState(() => {
    try { return localStorage.getItem("quote_builder_live_preview") !== "off"; } catch { return true; }
  });
  const [previewDevice, setPreviewDevice] = useState("desktop");
  const [showMarkupSettings, setShowMarkupSettings] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState("");
  const [emailCcRecipients, setEmailCcRecipients] = useState("");
  const [showCcField, setShowCcField] = useState(false);
  const [emailBody, setEmailBody] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [loadingEmailTemplate, setLoadingEmailTemplate] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [signatureType, setSignatureType] = useState("customer");
  const [signatureMethod, setSignatureMethod] = useState("type"); // "type" or "draw"
  const [typedSignature, setTypedSignature] = useState("");
  const [selectedPage, setSelectedPage] = useState(null);
  const [newPageType, setNewPageType] = useState("");
  // Import-from-another-template picker inside the Add Page modal — lets admins
  // reuse an Option / Add-on page that was already built on any other template
  // instead of rebuilding it from scratch.
  const [importSourceTplId, setImportSourceTplId] = useState("");
  const [importSearchTerm, setImportSearchTerm] = useState("");
  const [selectedAddons, setSelectedAddons] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({}); // structureKey -> optionPageId
  const [newLineItem, setNewLineItem] = useState({
    price_list_item_id: "", name: "", description: "",
    quantity: 1, unit: "", unit_price: 0, markup: 0, discount: 0, is_addon: false
  });
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);

  // Local markup/tax settings for this quote
  const [markupSettings, setMarkupSettings] = useState({
    default_markup: 0, tax_rate: 0, auto_markup_enabled: false
  });

  const loadQuote = useCallback(async () => {
    if (isNew) return;
    try {
      const response = await axios.get(`${API_URL}/quotes/${id}`);
      setQuote(response.data);
      // Initialize addon selections from existing line items
      const addonMap = {};
      (response.data.line_items || []).forEach(item => {
        if (item.is_addon) addonMap[item.id] = true;
      });
      // Also load saved page-level addon selections
      (response.data.selected_addon_pages || []).forEach(pageId => {
        addonMap[pageId] = true;
      });
      setSelectedAddons(addonMap);
      // Load saved option selections
      if (response.data.selected_options && Object.keys(response.data.selected_options).length > 0) {
        setSelectedOptions(response.data.selected_options);
      } else {
        // Auto-select first option per structure group
        const pages = response.data.pages || [];
        const initOptions = {};
        let currentStructureKey = "default_0";
        let groupIdx = 0;
        pages.forEach(page => {
          if (page.page_type === "structure") {
            currentStructureKey = page.id;
            groupIdx++;
          } else if (page.page_type === "option") {
            const key = currentStructureKey;
            if (!initOptions[key]) initOptions[key] = page.id;
          }
        });
        setSelectedOptions(initOptions);
      }
    } catch (error) {
      toast.error("Quote not found");
      navigate(backPath);
    }
    setLoading(false);
  }, [id, isNew, navigate]);

  useEffect(() => {
    loadQuote();
    fetchContacts();
    fetchProjects();
    fetchPriceList();
    fetchQuoteTemplates();
    fetchQuoteSettings();
  }, [loadQuote, fetchContacts, fetchProjects, fetchPriceList, fetchQuoteTemplates, fetchQuoteSettings]);

  // Poll email engagement (delivery / opens / clicks) every 60s so admins see
  // updates from Resend webhooks without having to refresh the page.
  useEffect(() => {
    if (isNew || !id) return;
    let cancelled = false;
    const fetchEngagement = async () => {
      try {
        const res = await axios.get(`${API_URL}/quotes/${id}/email-engagement`);
        if (!cancelled) setEmailEngagement(res.data);
      } catch { /* silent — endpoint may not exist on older deployments */ }
    };
    fetchEngagement();
    const t = setInterval(fetchEngagement, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, [id, isNew]);

  // Poll follow-up workflow status so the admin sees where the customer is in
  // the automated sequence (e.g. "Day 3 follow-up · next: tomorrow 9am").
  const fetchFollowup = useCallback(async () => {
    if (isNew || !id) return;
    try {
      const res = await axios.get(`${API_URL}/quotes/${id}/followup-status`);
      setFollowupStatus(res.data);
    } catch { /* silent */ }
  }, [id, isNew]);
  useEffect(() => {
    if (isNew || !id) return;
    fetchFollowup();
    const t = setInterval(fetchFollowup, 60000);
    return () => clearInterval(t);
  }, [id, isNew, fetchFollowup]);

  const stopFollowup = async (executionId) => {
    try {
      await axios.post(`${API_URL}/workflow-executions/${executionId}/stop`);
      toast.success("Follow-up sequence stopped — no more emails will go out.");
      setFollowupPopoverOpen(false);
      fetchFollowup();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to stop follow-up");
    }
  };

  useEffect(() => {
    if (quoteSettings) {
      setMarkupSettings({
        default_markup: quoteSettings.default_markup || 0,
        tax_rate: quoteSettings.tax_rate || 0,
        auto_markup_enabled: quoteSettings.auto_markup_enabled || false
      });
    }
  }, [quoteSettings]);

  // Auto-recalculate totals when line items, pages, adjustments, margin, tax, or discount change
  useEffect(() => {
    const lineItems = quote.line_items || [];
    const baseItems = lineItems.filter(i => !i.is_addon);
    const addonItems = lineItems.filter(i => i.is_addon && selectedAddons[i.id]);
    const activeItems = [...baseItems, ...addonItems];

    // Legacy flat-pricing subtotal
    let subtotal = 0;
    activeItems.forEach(item => {
      const itemTotal = (item.quantity || 1) * (item.unit_price || 0);
      const withMarkup = itemTotal * (1 + (item.markup || 0) / 100);
      subtotal += withMarkup - (item.discount || 0);
    });

    // Template-based pages — compute per-page final price using same rules as View & Sign
    const profitPct = parseFloat(quote.profit_margin_pct) || 0;
    const taxPct = parseFloat(quote.vendor_tax_pct) || 0;
    const pricingMode = quote.pricing_mode === "markup" ? "markup" : "margin";
    const pricePage = (page) => {
      const c = page.content || {};
      const items = (c.line_items || []).filter(it => it.visible !== false);
      const itemsSum = items.reduce((sum, it) => {
        const q = parseFloat(it.quantity) || 0;
        const w = parseFloat(it.waste_factor) || 0;
        const pr = parseFloat(it.customer_price || it.unit_price) || 0;
        const m = parseFloat(it.margin) || 0;
        return sum + q * (1 + w / 100) * pr * (1 + m / 100);
      }, 0);
      const manualBase = parseFloat(c.base_price || c.manual_price) || 0;
      const raw = manualBase > 0 ? manualBase : itemsSum;
      const optMkp = parseFloat(c.price_markup_pct) || 0;
      const optAdj = parseFloat(c.price_adjustment) || 0;
      // Manual base = final; otherwise apply the selected profit mode (margin or markup) + tax
      const base = manualBase > 0 ? raw : (applyProfit(raw, profitPct, pricingMode) + raw * (taxPct / 100));
      return base + (base * optMkp / 100) + optAdj;
    };

    const allPages = (quote.pages || []).filter(p => p.enabled !== false);
    const structurePages = allPages.filter(p => p.page_type === "structure");
    const optionPages = allPages.filter(p => p.page_type === "option");
    const addonPages = allPages.filter(p => p.page_type === "addon");

    const structureTotal = structurePages.reduce((s, p) => s + pricePage(p), 0);
    // Default to the highest-priced option (best-case customer total for email preview)
    const optionTotals = optionPages.map(pricePage);
    const firstOption = optionTotals.length > 0 ? Math.max(...optionTotals) : 0;
    // Default to all addons selected (worst-case for email so customer sees max)
    const addonTotal = addonPages.reduce((s, p) => s + pricePage(p), 0);

    const templateSubtotal = structureTotal + firstOption + addonTotal;

    // Grand subtotal — use flat-pricing OR template total (whichever is non-zero)
    const grandSubtotal = templateSubtotal > 0 ? templateSubtotal : subtotal;

    // Quote-level discount
    const discount = parseFloat(quote.discount_amount) || 0;

    // Global markup + adjust (from Pricing tab)
    const globalMkp = parseFloat(quote.price_markup_pct) || 0;
    const globalAdj = parseFloat(quote.price_adjustment) || 0;
    const withGlobal = grandSubtotal + (grandSubtotal * globalMkp / 100) + globalAdj;

    // Flat-pricing tax (only applies when not using template)
    const taxAmount = templateSubtotal === 0
      ? (subtotal + (markupSettings.auto_markup_enabled ? subtotal * (markupSettings.default_markup / 100) : 0)) * (markupSettings.tax_rate / 100)
      : 0;
    const markupAmount = (templateSubtotal === 0 && markupSettings.auto_markup_enabled) ? subtotal * (markupSettings.default_markup / 100) : 0;

    const total = Math.max(0, withGlobal + taxAmount - discount);

    setQuote(prev => ({
      ...prev,
      subtotal: Math.round(grandSubtotal * 100) / 100,
      markup_amount: Math.round(markupAmount * 100) / 100,
      tax_amount: Math.round(taxAmount * 100) / 100,
      total: Math.round(total * 100) / 100
    }));
  }, [quote.line_items, quote.pages, quote.profit_margin_pct, quote.vendor_tax_pct, quote.discount_amount, quote.price_markup_pct, quote.price_adjustment, quote.pricing_mode, selectedAddons, selectedOptions, markupSettings]);

  const handleSave = async () => {
    if (!quote.project_id || !quote.contact_id) {
      toast.error("Please select a project and contact");
      return;
    }
    setSaving(true);
    try {
      const saveData = {
        ...quote,
        selected_options: selectedOptions,
        selected_addons: Object.entries(selectedAddons).filter(([_, v]) => v).map(([k]) => k),
      };
      if (isNew) {
        const created = await createQuote(saveData);
        toast.success("Quote created!");
        navigate(`/quotes/${created.id}`);
      } else {
        await updateQuote(id, saveData);
        toast.success("Quote saved!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save quote");
    }
    setSaving(false);
  };

  const handleCalculate = async () => {
    if (isNew) { toast.error("Save first"); return; }
    try {
      const result = await calculateQuote(id);
      setQuote(result);
      toast.success("Totals recalculated!");
    } catch (error) { toast.error("Failed to calculate"); }
  };

  // Metadata autosave — only debounces `notes` + `valid_until` so we don't
  // interfere with pricing recalcs on `pages` / `line_items`. Existing
  // quotes only (new quotes need explicit Save to get an id).
  const metadata = React.useMemo(
    () => ({ notes: quote.notes || "", valid_until: quote.valid_until || "" }),
    [quote.notes, quote.valid_until],
  );
  const { status: autosaveStatus, savedAt, saveNow: saveMetadataNow } = useAutosave(
    metadata,
    async (value) => {
      // Uses the lightweight PATCH endpoint so we don't send the entire
      // quote payload on every keystroke (would fight the pricing engine).
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API_URL}/quotes/${id}/metadata`,
        value,
        { headers: { Authorization: `Bearer ${token}` } },
      );
    },
    { delay: 900, enabled: !isNew && !!id },
  );

  // Flush pending metadata edits on unmount / nav away.
  useEffect(() => {
    return () => { saveMetadataNow(); };
  }, [saveMetadataNow]);

  const handleStatusChange = async (newStatus) => {
    if (isNew) return;
    try {
      const updated = await updateQuoteStatus(id, newStatus);
      setQuote(updated);
      toast.success(`Quote marked as ${newStatus}!`);
    } catch (error) { toast.error("Failed to update status"); }
  };

  const handleUnsign = async () => {
    if (isNew || !quote?.id) return;
    setUnsigning(true);
    try {
      const res = await axios.post(`${API_URL}/quotes/${quote.id}/unsign`);
      const { quote: updatedQuote, deposit_invoice_warning } = res.data || {};
      if (updatedQuote) setQuote(updatedQuote);
      setShowUnsignConfirm(false);
      if (deposit_invoice_warning) {
        toast.warning(
          `Signatures cleared. Heads up: Invoice #${deposit_invoice_warning.invoice_number || ""} was already created (${deposit_invoice_warning.status}). Void it manually if needed.`,
          { duration: 8000 }
        );
      } else {
        toast.success("Signatures cleared — you can now edit and re-send this quote.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to undo signing");
    } finally {
      setUnsigning(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (isNew) { toast.error("Save the quote first"); return; }
    const quoteUuid = quote.id || id;
    const filename = `Quote-${quote.quote_number || quoteUuid}.pdf`;
    try {
      const res = await axios.get(`${API_URL}/public/quotes/${quoteUuid}/pdf`, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.setAttribute("download", filename);
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 5000);
      toast.success("PDF downloaded!");
    } catch (err) {
      console.error("[PDF] Download failed:", err);
      toast.error(err?.response?.status ? `Failed to generate PDF (${err.response.status})` : "Failed to generate PDF");
    }
  };

  const handleOpenEmailModal = async () => {
    setShowEmailModal(true);
    setLoadingEmailTemplate(true);
    // Sensible defaults so the modal is never blank even if the API hiccups
    const companyName = (quote && quote.company_name) || "Tides Team Exteriors";
    const first = (contact?.name || "").split(" ")[0] || "there";
    const fallbackSubject = `Your Quote from ${companyName}${quote.quote_number ? ` - ${quote.quote_number}` : ""}`;
    const fallbackBody = (
      `Hello ${first},\n\n` +
      `Thank you for your interest in our services. ` +
      `Please find your quote attached to this email. ` +
      `You can also view and approve it online using the link below.\n\n` +
      `If you have any questions, please don't hesitate to reach out.\n\n` +
      `Best regards,\n${companyName}`
    );
    try {
      const res = await axios.get(`${API_URL}/quotes/${id}/email-template`);
      setEmailRecipient(res.data.recipient_email || contact?.email || "");
      setEmailBody(res.data.email_body || fallbackBody);
      setEmailSubject(res.data.subject || fallbackSubject);
    } catch (err) {
      console.error("[Email template] Failed to load:", err);
      setEmailRecipient(contact?.email || "");
      setEmailBody(fallbackBody);
      setEmailSubject(fallbackSubject);
      toast.error("Couldn't load saved email template — filled in a starter you can edit.");
    }
    setLoadingEmailTemplate(false);
  };

  const handleSendTestToMe = async () => {
    if (!user?.email) { toast.error("Your account has no email set"); return; }
    if (isNew) { toast.error("Save the quote first"); return; }
    setSendingTest(true);
    try {
      // Save first so the email body reflects the latest pricing/discounts
      if (quote.project_id && quote.contact_id) {
        try {
          const saveData = {
            ...quote,
            selected_options: selectedOptions,
            selected_addons: Object.entries(selectedAddons).filter(([_, v]) => v).map(([k]) => k),
          };
          await updateQuote(id, saveData);
        } catch (saveErr) {
          console.warn("Pre-test save failed, proceeding anyway:", saveErr);
        }
      }
      // Fetch template-rendered body so the test email looks exactly like a real send
      let body = "";
      let subject = "";
      try {
        const tplRes = await axios.get(`${API_URL}/quotes/${id}/email-template`);
        body = tplRes.data.email_body || "";
        subject = tplRes.data.subject || "";
      } catch (e) { /* fall through with empty body */ }
      const testRes = await axios.post(`${API_URL}/quotes/${id}/send-email`, {
        recipient_email: user.email,
        email_body: body,
        subject: `[TEST] ${subject || "Your Quote"}`,
        frontend_url: window.location.origin,
        is_test: true,
      });
      toast.success(`Test quote sent to ${user.email}`);
      trackEmailDelivery(testRes.data?.email_record_id, user.email);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test");
    }
    setSendingTest(false);
  };

  const handleSpamCheck = async () => {
    setCheckingSpam(true);
    try {
      const res = await axios.post(`${API_URL}/quotes/${id}/spam-check`, {
        subject: emailSubject,
        html_body: emailBody,
      });
      setSpamScore(res.data);
    } catch (e) {
      toast.error("Spam check failed");
    } finally {
      setCheckingSpam(false);
    }
  };

  const handleSendEmail = async () => {
    if (!emailRecipient) { toast.error("Enter recipient email"); return; }
    setSendingEmail(true);
    try {
      // Save the quote first so the freshly-recalculated total (and any pricing tweaks) persists
      // before the backend generates the email body.
      if (!isNew && quote.project_id && quote.contact_id) {
        try {
          const saveData = {
            ...quote,
            selected_options: selectedOptions,
            selected_addons: Object.entries(selectedAddons).filter(([_, v]) => v).map(([k]) => k),
          };
          await updateQuote(id, saveData);
        } catch (saveErr) {
          console.warn("Pre-send save failed, proceeding anyway:", saveErr);
        }
      }
      const sendRes = await axios.post(`${API_URL}/quotes/${id}/send-email`, {
        recipient_email: emailRecipient,
        cc_emails: emailCcRecipients,
        email_body: emailBody,
        subject: emailSubject,
        frontend_url: window.location.origin,
      });
      setQuote(prev => ({ ...prev, status: "sent", sent_at: new Date().toISOString() }));
      const ccPreview = (emailCcRecipients || "").trim();
      toast.success(ccPreview ? `Quote sent to ${emailRecipient} (cc: ${ccPreview})` : `Quote sent to ${emailRecipient}!`);
      setShowEmailModal(false);
      setEmailRecipient("");
      setEmailCcRecipients("");
      setShowCcField(false);
      // Live delivery polling — the toast will flip to delivered / bounced / suppressed
      trackEmailDelivery(sendRes.data?.email_record_id, emailRecipient);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed to send email"); }
    setSendingEmail(false);
  };

  const handleAddPage = () => {
    if (!newPageType) return;
    const pageConfig = PAGE_TYPES.find(p => p.value === newPageType);
    const newPage = {
      id: `page-${Date.now()}`,
      page_type: newPageType,
      title: pageConfig?.label || newPageType,
      content: getDefaultPageContent(newPageType),
      order: quote.pages.length
    };
    setQuote(prev => ({ ...prev, pages: [...prev.pages, newPage] }));
    setShowPageModal(false);
    setNewPageType("");
    setSelectedPage(newPage);
  };

  // Import an existing Option / Add-on page from another template into this
  // quote. Deep-clones content + gives everything fresh ids so edits on the
  // copy don't leak back into the source template. Resets any "linked to
  // another option" pointer since the referenced target won't exist here.
  const handleImportPageFromTemplate = (sourcePage) => {
    if (!sourcePage) return;
    const cloneContent = JSON.parse(JSON.stringify(sourcePage.content || {}));
    if (Array.isArray(cloneContent.line_items)) {
      cloneContent.line_items = cloneContent.line_items.map(li => ({
        ...li,
        id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      }));
    }
    if (Array.isArray(cloneContent.structures)) {
      // Fresh ids for structure groups + remap line item structure_group refs
      const idMap = {};
      cloneContent.structures = cloneContent.structures.map(g => {
        const newId = `sg-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        idMap[g.id] = newId;
        return { ...g, id: newId };
      });
      if (Array.isArray(cloneContent.line_items)) {
        cloneContent.line_items = cloneContent.line_items.map(li => ({
          ...li,
          structure_group: idMap[li.structure_group] || li.structure_group || "",
        }));
      }
    }
    // Reset cross-page link — target option lives in the source template, not
    // in this quote, so leaving the pointer would produce a broken price.
    if (sourcePage.page_type === "option") {
      cloneContent.linked_to_option_id = "";
      cloneContent.linked_to_option_disabled = true;
    }
    const newPage = {
      id: `page-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      page_type: sourcePage.page_type,
      title: sourcePage.title || (sourcePage.page_type === "option" ? "Option" : "Add-on"),
      content: cloneContent,
      order: quote.pages.length,
      enabled: true,
      mobile_layout: sourcePage.mobile_layout || "auto",
    };
    setQuote(prev => ({ ...prev, pages: [...prev.pages, newPage] }));
    setShowPageModal(false);
    setNewPageType("");
    setImportSourceTplId("");
    setImportSearchTerm("");
    setSelectedPage(newPage);
    toast.success(`Imported "${newPage.title}" into this quote`);
  };

  const getDefaultPageContent = (pageType) => {
    switch (pageType) {
      case "cover": return { headline: "PROPOSAL", subheadline: "Prepared for:", show_logo: true, show_date: true, show_quote_number: true };
      case "text": return { title: "Terms & Conditions", body: "", sections: [] };
      case "pricing": return { show_line_items: true, show_subtotal: true, show_tax: true, show_total: true };
      case "custom": return { title: "", blocks: [] };
      case "structure": return { title: "", line_items: [], structures: [], tax_rate: 0, show_cash_discount: true };
      case "option": return { option_name: "", option_tag: "", description: "", scope: "", important_info: "", line_items: [], structures: [], tax_rate: 9, show_cash_discount: true, show_price: true, show_scope: true, show_warranty: true, show_discounts: true };
      case "addon": return { addon_name: "", price: 0, description: "", line_items: [], structures: [], tax_rate: 9, show_cash_discount: true, show_price: true, default_selected: false };
      case "signature": return { customer_label: "Customer Signature", admin_label: "Company Signature", show_date: true, show_printed_name: true, show_options: true };
      default: return {};
    }
  };

  const handleRemovePage = (pageId) => {
    setQuote(prev => ({ ...prev, pages: prev.pages.filter(p => p.id !== pageId) }));
    if (selectedPage?.id === pageId) setSelectedPage(null);
  };

  const handleDuplicatePage = (pageId) => {
    setQuote(prev => {
      const idx = prev.pages.findIndex(p => p.id === pageId);
      if (idx < 0) return prev;
      const src = prev.pages[idx];
      // Deep-clone content + line items so edits to the copy don't mutate the original
      const cloneContent = JSON.parse(JSON.stringify(src.content || {}));
      // Give any nested line_items fresh ids so linking / selection state stays sane
      if (Array.isArray(cloneContent.line_items)) {
        cloneContent.line_items = cloneContent.line_items.map(li => ({
          ...li,
          id: `li-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        }));
      }
      // Reset "linked to another option" pointer so the duplicate is standalone
      if (src.page_type === "option") {
        cloneContent.linked_to_option_id = "";
        cloneContent.linked_to_option_offset = 0;
      }
      const copy = {
        ...src,
        id: `page-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        title: `${src.title || "Page"} (Copy)`,
        content: {
          ...cloneContent,
          option_name: cloneContent.option_name ? `${cloneContent.option_name} (Copy)` : cloneContent.option_name,
          addon_name: cloneContent.addon_name ? `${cloneContent.addon_name} (Copy)` : cloneContent.addon_name,
          default_selected: false,
        },
        enabled: src.enabled,
      };
      const nextPages = [
        ...prev.pages.slice(0, idx + 1),
        copy,
        ...prev.pages.slice(idx + 1),
      ].map((p, i) => ({ ...p, order: i }));
      // Focus the new copy so admin can edit it right away
      setTimeout(() => setSelectedPage(copy), 0);
      return { ...prev, pages: nextPages };
    });
    toast.success("Page duplicated");
  };

  const handleMovePage = (pageId, direction) => {
    setQuote(prev => {
      const pages = [...prev.pages];
      const idx = pages.findIndex(p => p.id === pageId);
      if (idx < 0) return prev;
      const newIdx = idx + direction;
      if (newIdx < 0 || newIdx >= pages.length) return prev;
      [pages[idx], pages[newIdx]] = [pages[newIdx], pages[idx]];
      return { ...prev, pages };
    });
  };

  const handleDragEndPages = (result) => {
    if (!result.destination) return;
    const from = result.source.index;
    const to = result.destination.index;
    if (from === to) return;
    setQuote(prev => {
      const pages = [...prev.pages];
      const [moved] = pages.splice(from, 1);
      pages.splice(to, 0, moved);
      return { ...prev, pages };
    });
  };

  const handleUpdatePageContent = (pageId, content) => {
    setQuote(prev => ({
      ...prev,
      pages: prev.pages.map(p => p.id === pageId ? { ...p, content: { ...p.content, ...content } } : p)
    }));
  };

  const handleAddLineItem = () => {
    const item = {
      ...newLineItem,
      id: `item-${Date.now()}`,
      total: newLineItem.quantity * newLineItem.unit_price * (1 + newLineItem.markup / 100) - newLineItem.discount
    };
    setQuote(prev => ({ ...prev, line_items: [...prev.line_items, item] }));
    if (item.is_addon) setSelectedAddons(prev => ({ ...prev, [item.id]: false }));
    setNewLineItem({ price_list_item_id: "", name: "", description: "", quantity: 1, unit: "", unit_price: 0, markup: 0, discount: 0, is_addon: false });
    setShowLineItemModal(false);
  };

  const handleRemoveLineItem = (itemId) => {
    setQuote(prev => ({ ...prev, line_items: prev.line_items.filter(i => i.id !== itemId) }));
    setSelectedAddons(prev => { const n = { ...prev }; delete n[itemId]; return n; });
  };

  const handlePriceListSelect = (itemId) => {
    const priceItem = priceList.find(p => p.id === itemId);
    if (priceItem) {
      setNewLineItem(prev => ({
        ...prev, price_list_item_id: itemId, name: priceItem.name,
        description: priceItem.description, unit: priceItem.unit, unit_price: priceItem.base_price
      }));
    }
  };

  const handleProjectSelect = (projectId) => {
    const project = projects.find(p => p.id === projectId);
    setQuote(prev => ({ ...prev, project_id: projectId, contact_id: project?.contact_id || prev.contact_id }));
    // Auto-apply default template for service type
    if (project?.service_type) {
      const defaultTemplate = quoteTemplates.find(t => t.service_type === project.service_type && t.is_default);
      if (defaultTemplate && isNew && quote.pages.length === 0) {
        const settings = defaultTemplate.settings || {};
        const expiryDays = settings.quote_expiration_days || 30;
        const validUntil = new Date(Date.now() + expiryDays * 86400000).toISOString().split('T')[0];
        setQuote(prev => ({ ...prev, template_id: defaultTemplate.id, pages: defaultTemplate.pages || [], valid_until: validUntil, profit_margin_pct: prev.profit_margin_pct || (settings.profit_margin ?? 40), vendor_tax_pct: prev.vendor_tax_pct ?? settings.vendor_tax_pct ?? 0 }));
        if (settings.profit_margin !== undefined) {
          setMarkupSettings(prev => ({ ...prev, default_markup: settings.profit_margin || 0 }));
        }
        toast.success(`Template "${defaultTemplate.name}" auto-applied!`);
      }
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = quoteTemplates.find(t => t.id === templateId);
    if (template) {
      const settings = template.settings || {};
      const expiryDays = settings.quote_expiration_days || 30;
      const validUntil = new Date(Date.now() + expiryDays * 86400000).toISOString().split('T')[0];
      setQuote(prev => ({ ...prev, template_id: templateId, pages: template.pages || [], valid_until: validUntil, profit_margin_pct: settings.profit_margin ?? 40, vendor_tax_pct: settings.vendor_tax_pct ?? 0 }));
      // Apply template markup settings to the quote's markup settings
      if (settings.profit_margin !== undefined) {
        setMarkupSettings(prev => ({ ...prev, default_markup: settings.profit_margin || 0 }));
      }
      toast.success("Template applied!");
    }
  };

  // Signature handlers
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.strokeStyle = '#0F4C81';
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => setIsDrawing(false);

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setTypedSignature("");
  };

  const handleSignQuote = async (name) => {
    let signatureData;
    if (signatureMethod === "type") {
      // Generate a signature image from typed name
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 100;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, 400, 100);
      ctx.font = 'italic 36px "Georgia", serif';
      ctx.fillStyle = '#0F4C81';
      ctx.fillText(typedSignature || name, 20, 60);
      signatureData = canvas.toDataURL();
    } else {
      const canvas = signatureCanvasRef.current;
      if (!canvas) return;
      signatureData = canvas.toDataURL();
    }

    try {
      const updated = await signQuote(id, signatureType, { signature: signatureData, name: name || typedSignature });
      setQuote(updated);
      setShowSignatureModal(false);
      setTypedSignature("");
      toast.success(`${signatureType === 'customer' ? 'Customer' : 'Admin'} signature added!`);
    } catch (error) { toast.error("Failed to sign quote"); }
  };

  const toggleAddon = (itemId) => {
    setSelectedAddons(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const handleCopyShareLink = () => {
    const url = `${window.location.origin}/public/quote/${id}`;
    navigator.clipboard.writeText(url);
    toast.success("Public link copied to clipboard!");
  };

  const contact = contacts.find(c => c.id === quote.contact_id);
  const project = projects.find(p => p.id === quote.project_id);

  const baseItems = (quote.line_items || []).filter(i => !i.is_addon);
  const addonItems = (quote.line_items || []).filter(i => i.is_addon);

  // Check if this quote has template-based per-page pricing
  const pricingPages = (quote.pages || []).filter(p =>
    (p.page_type === "option" || p.page_type === "addon" || p.page_type === "structure") && p.enabled !== false
  );
  const hasPagePricing = pricingPages.some(p => p.page_type === "option" || p.page_type === "addon");

  // Helper to update a line item within a page
  const updatePageLineItem = (pageId, itemIdx, field, value) => {
    setQuote(prev => ({
      ...prev,
      pages: prev.pages.map(p => {
        if (p.id !== pageId) return p;
        const items = [...(p.content.line_items || [])];
        items[itemIdx] = { ...items[itemIdx], [field]: value };
        return { ...p, content: { ...p.content, line_items: items } };
      })
    }));
  };

  // Helper to toggle cash discount on a page
  const togglePageCashDiscount = (pageId, value) => {
    setQuote(prev => ({
      ...prev,
      pages: prev.pages.map(p => p.id !== pageId ? p : { ...p, content: { ...p.content, show_cash_discount: value } })
    }));
  };

  // Helper to update tax rate on a page
  const updatePageTaxRate = (pageId, value, extraContentUpdates) => {
    setQuote(prev => ({
      ...prev,
      pages: prev.pages.map(p => p.id !== pageId ? p : { ...p, content: { ...p.content, tax_rate: value, ...(extraContentUpdates || {}) } })
    }));
  };

  const updatePageContent = (pageId, newContent) => {
    setQuote(prev => ({
      ...prev,
      pages: prev.pages.map(p => p.id !== pageId ? p : { ...p, content: newContent })
    }));
  };

  // Reset every line item's margin on a page back to its Price List default (0 if unlinked).
  const resetPageMarginsToPriceList = (pageId) => {
    setQuote(prev => {
      const priceMap = {};
      (priceList || []).forEach(pl => { priceMap[pl.id] = pl; });
      let changedCount = 0;
      const newPages = prev.pages.map(p => {
        if (p.id !== pageId) return p;
        const items = (p.content?.line_items || []).map(it => {
          const pl = it.price_list_id ? priceMap[it.price_list_id] : null;
          const defaultMargin = pl ? (parseFloat(pl.margin) || 0) : 0;
          const currentMargin = parseFloat(it.margin) || 0;
          if (defaultMargin !== currentMargin) changedCount += 1;
          return { ...it, margin: defaultMargin };
        });
        return { ...p, content: { ...p.content, line_items: items } };
      });
      if (changedCount === 0) {
        toast.info("All margins already match Price List defaults");
        return prev;
      }
      toast.success(`Reset ${changedCount} item${changedCount === 1 ? "" : "s"} to Price List defaults`);
      return { ...prev, pages: newPages };
    });
  };

  // Helper to set manual price on a page without line items
  const handleSetPagePrice = (pageId, price) => {
    setQuote(prev => {
      const updatedPages = prev.pages.map(p => p.id !== pageId ? p : { ...p, content: { ...p.content, base_price: price, manual_price: price } });
      // Recalculate total
      const pages = updatedPages;
      const calcPageRev = (p) => {
        const items = p.content?.line_items || [];
        const taxRate = parseFloat(p.content?.tax_rate) || 0;
        const manual = parseFloat(p.content?.base_price || p.content?.manual_price) || 0;
        if (manual > 0) {
          return manual + manual * (taxRate / 100);
        }
        if (items.length > 0) {
          const sub = items.reduce((sum, it) => {
            const q = parseFloat(it.quantity) || 0;
            const pr = parseFloat(it.customer_price || it.unit_price) || 0;
            const m = parseFloat(it.margin) || 0;
            const w = parseFloat(it.waste_factor) || 0;
            return sum + q * (1 + w / 100) * pr * (1 + m / 100);
          }, 0);
          return sub + sub * (taxRate / 100);
        }
        return 0;
      };
      const structureTotal = pages.filter(p => p.page_type === "structure").reduce((s, p) => s + calcPageRev(p), 0);
      const optionTotals = pages.filter(p => p.page_type === "option").map(p => calcPageRev(p));
      const addonTotal = pages.filter(p => p.page_type === "addon").reduce((s, p) => s + calcPageRev(p), 0);
      const firstOption = optionTotals[0] || 0;
      const totalRaw = structureTotal + firstOption + addonTotal;
      const profitPct = parseFloat(prev.profit_margin_pct) || 0;
      const taxPct = parseFloat(prev.vendor_tax_pct) || 0;
      const mode = prev.pricing_mode === "markup" ? "markup" : "margin";
      const withMargin = applyProfit(totalRaw, profitPct, mode);
      const vendorTax = totalRaw * (taxPct / 100);
      const baseTotal = withMargin + vendorTax;
      const pct = parseFloat(prev.price_markup_pct) || 0;
      const markupAmt = baseTotal * (pct / 100);
      const adj = parseFloat(prev.price_adjustment) || 0;
      return { ...prev, pages: updatedPages, total: Math.round((baseTotal + markupAmt + adj) * 100) / 100 };
    });
  };

  // Helper to update price adjustment or markup
  const handlePriceAdjustmentChange = (amount, label, markupPct) => {
    setQuote(prev => {
      const adj = amount !== null && amount !== undefined ? (parseFloat(amount) || 0) : (parseFloat(prev.price_adjustment) || 0);
      const newLabel = label !== undefined && label !== null ? label : (prev.price_adjustment_label || "");
      const pct = markupPct !== null && markupPct !== undefined ? (parseFloat(markupPct) || 0) : (parseFloat(prev.price_markup_pct) || 0);
      const pages = prev.pages || [];
      const calcPageRev = (p) => {
        const manual = parseFloat(p.content?.base_price || p.content?.manual_price) || 0;
        if (manual > 0) return manual;
        const items = p.content?.line_items || [];
        if (items.length > 0) {
          return items.reduce((sum, it) => {
            const q = parseFloat(it.quantity) || 0;
            const pr = parseFloat(it.customer_price || it.unit_price) || 0;
            const m = parseFloat(it.margin) || 0;
            const w = parseFloat(it.waste_factor) || 0;
            return sum + q * (1 + w / 100) * pr * (1 + m / 100);
          }, 0);
        }
        return 0;
      };
      const structureTotal = pages.filter(p => p.page_type === "structure").reduce((s, p) => s + calcPageRev(p), 0);
      const optionTotals = pages.filter(p => p.page_type === "option").map(p => calcPageRev(p));
      const addonTotal = pages.filter(p => p.page_type === "addon").reduce((s, p) => s + calcPageRev(p), 0);
      const firstOption = optionTotals[0] || 0;
      const totalRaw = structureTotal + firstOption + addonTotal;
      const profitPct2 = parseFloat(prev.profit_margin_pct) || 0;
      const taxPct2 = parseFloat(prev.vendor_tax_pct) || 0;
      const mode2 = prev.pricing_mode === "markup" ? "markup" : "margin";
      const withMargin = applyProfit(totalRaw, profitPct2, mode2);
      const vendorTax = totalRaw * (taxPct2 / 100);
      const baseTotal = withMargin + vendorTax;
      const markupAmt = baseTotal * (pct / 100);
      return {
        ...prev,
        price_adjustment: adj,
        price_adjustment_label: newLabel,
        price_markup_pct: pct,
        total: Math.round((baseTotal + markupAmt + adj) * 100) / 100,
      };
    });
  };

  // Calculate per-item total
  // Calculate raw item cost (no vendor tax, no card fee — those are applied globally)
  const calcItemTotal = (it) => {
    const qty = parseFloat(it.quantity) || 0;
    const price = parseFloat(it.unit_price) || 0;
    const margin = parseFloat(it.margin) || 0;
    const waste = parseFloat(it.waste_factor) || 0;
    const effectiveQty = qty * (1 + waste / 100);
    return effectiveQty * price * (1 + margin / 100);
  };

  // -----------------------------------------------------------------
  // Add-on cost/revenue helpers — used to fold selected add-ons into
  // the option-level Private Profit Breakdown so the admin can see the
  // true estimated profit for the option + everything the customer
  // opted into.
  // -----------------------------------------------------------------
  const calcAddonPageCost = (page) => {
    const items = (page.content?.line_items || []).filter(it => it.visible !== false);
    return items.reduce((sum, it) => {
      const q = parseFloat(it.quantity) || 0;
      const w = parseFloat(it.waste_factor) || 0;
      const up = parseFloat(it.unit_price) || 0;
      return sum + q * (1 + w / 100) * up;
    }, 0);
  };

  const calcAddonPageRevenue = (page) => {
    const c = page.content || {};
    const items = (c.line_items || []).filter(it => it.visible !== false);
    const manualBase = parseFloat(c.base_price || c.manual_price) || 0;
    const itemsSum = items.reduce((sum, it) => {
      const q = parseFloat(it.quantity) || 0;
      const pr = parseFloat(it.customer_price || it.unit_price) || 0;
      const m = parseFloat(it.margin) || 0;
      const w = parseFloat(it.waste_factor) || 0;
      return sum + q * (1 + w / 100) * pr * (1 + m / 100);
    }, 0);
    const taxRate = parseFloat(c.tax_rate) || 0;
    const sub = manualBase > 0 ? manualBase : itemsSum;
    const tot = manualBase > 0 ? manualBase : sub + sub * (taxRate / 100);
    const bp = parseFloat(c.price);
    const rawPrice = tot > 0 ? tot : (bp || 0);
    const addonMarkupPct = parseFloat(c.price_markup_pct) || 0;
    const addonAdjustment = parseFloat(c.price_adjustment) || 0;
    return rawPrice + (rawPrice * addonMarkupPct / 100) + addonAdjustment;
  };

  // Calculate the full customer price for an option page
  // Formula (ProLine-style true gross margin):
  //   price = raw_costs / (1 - profit_margin/100) + raw_costs * vendor_tax/100
  // Margin formula gives true gross margin on price (NOT a markup multiplier).
  const calcFullOptionPrice = (page, _seen = new Set()) => {
    const c = page.content || {};

    // ------------------------------------------------------------------
    // Linked-option pricing: this option's total is another option's
    // total plus an offset (e.g. IKO Dynasty = Tamko Titan − $100).
    // Uses `_seen` to guard against cycles (A→B→A). Ignored per-quote if
    // the admin has toggled the link off via `linked_to_option_disabled`.
    // ------------------------------------------------------------------
    const linkTargetId = c.linked_to_option_id || "";
    const linkOffset = parseFloat(c.linked_offset);
    if (linkTargetId && !c.linked_to_option_disabled && !_seen.has(page.id)) {
      const target = (quote.pages || []).find(p => p.id === linkTargetId && p.page_type === "option" && p.enabled !== false);
      if (target) {
        const nextSeen = new Set(_seen);
        nextSeen.add(page.id);
        const targetPrice = calcFullOptionPrice(target, nextSeen);
        return Math.max(0, targetPrice + (Number.isFinite(linkOffset) ? linkOffset : 0));
      }
    }

    const items = (c.line_items || []).filter(it => it.visible !== false);
    const itemsSum = items.reduce((sum, it) => sum + calcItemTotal(it), 0);
    // Sells For override — if user typed a manual base price, use it as the raw price (no further margin/tax stacking)
    const manualBase = parseFloat(c.base_price || c.manual_price) || 0;
    const optRaw = manualBase > 0 ? manualBase : itemsSum;

    // Structure raw costs (no per-page tax). Skip when manual override is active so the user's
    // typed "Sells For" is authoritative and doesn't get inflated by structure items.
    const structureRaw = manualBase > 0 ? 0 : quote.pages
      .filter(p => p.page_type === "structure" && p.enabled !== false)
      .reduce((sum, sp) => {
        const sItems = (sp.content?.line_items || []).filter(it => it.visible !== false);
        return sum + sItems.reduce((s, it) => s + calcItemTotal(it), 0);
      }, 0);

    const totalRaw = optRaw + structureRaw;

    // When manual base price is set, treat it as the final sells-for (skip margin/tax stacking)
    if (manualBase > 0) {
      const mOptMkp = parseFloat(c.price_markup_pct) || 0;
      const mOptAdj = parseFloat(c.price_adjustment) || 0;
      return totalRaw + (totalRaw * mOptMkp / 100) + mOptAdj;
    }

    // Profit % on raw costs — mode is either "margin" (default) or "markup"
    const profitMarginPct = parseFloat(quote.profit_margin_pct) || 0;
    const pricingMode = quote.pricing_mode === "markup" ? "markup" : "margin";
    const withMargin = applyProfit(totalRaw, profitMarginPct, pricingMode);

    // Vendor tax on raw costs (not on margin)
    const vendorTaxPct = parseFloat(quote.vendor_tax_pct) || 0;
    const tax = totalRaw * (vendorTaxPct / 100);

    const subtotal = withMargin + tax;

    // Per-option adjustments
    const optMarkupPct = parseFloat(c.price_markup_pct) || 0;
    const optAdjust = parseFloat(c.price_adjustment) || 0;

    return subtotal + (subtotal * optMarkupPct / 100) + optAdjust;
  };

  // Build structure groups: options grouped by preceding structure pages
  const buildStructureGroups = () => {
    const pages = quote.pages || [];
    const groups = []; // { structure: null|page, options: [], addons: [] }
    let currentGroup = { structure: null, options: [] };
    const allAddons = [];

    pages.forEach(page => {
      if (page.page_type === "structure") {
        if (currentGroup.options.length > 0 || currentGroup.structure) {
          groups.push(currentGroup);
        }
        currentGroup = { structure: page, options: [] };
      } else if (page.page_type === "option") {
        currentGroup.options.push(page);
      } else if (page.page_type === "addon") {
        allAddons.push(page);
      }
    });
    if (currentGroup.options.length > 0 || currentGroup.structure) {
      groups.push(currentGroup);
    }
    return { groups, addons: allAddons };
  };

  const { groups: structureGroups, addons: addonPages } = buildStructureGroups();
  const allOptionPages = (quote.pages || []).filter(p => p.page_type === "option");

  // Calculate page total helper
  const calcPageTotal = (page) => {
    const items = page.content?.line_items || [];
    const taxRate = parseFloat(page.content?.tax_rate) || 0;
    const sub = items.reduce((sum, it) => sum + calcItemTotal(it), 0);
    return sub + sub * (taxRate / 100);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" /></div>;
  }

  return (
    <div className="min-h-screen">
      <Header title={isNew ? "New Quote" : `Quote ${quote.quote_number || ""}`}>
        <Button variant="outline" className="h-12" onClick={() => navigate(backPath)}><ArrowLeft className="w-5 h-5 mr-2" />{fromProject ? "Back to Project" : "Back"}</Button>
        {!isNew && (
          <>
            <Button
              variant="outline"
              className="h-12"
              onClick={handleDownloadPDF}
              data-testid="download-pdf-btn"
            >
              <Download className="w-5 h-5 mr-2" />PDF
            </Button>
            <Button variant="outline" className="h-12" onClick={() => { setPreviewDevice("desktop"); setShowCustomerPreview(true); }} data-testid="customer-preview-btn"><PenTool className="w-5 h-5 mr-2" />View & Sign</Button>
            <Button variant="outline" className="h-12" onClick={handleCopyShareLink} data-testid="share-link-btn"><Link className="w-5 h-5 mr-2" />Share</Button>
            <Button variant="outline" className="h-12" onClick={handleOpenEmailModal} data-testid="email-quote-btn"><Mail className="w-5 h-5 mr-2" />Email</Button>
            <Button
              variant="outline"
              className="h-12 border-amber-300 text-amber-700 hover:bg-amber-50 hover:text-amber-800"
              onClick={handleSendTestToMe}
              disabled={sendingTest || isNew}
              title={user?.email ? `Sends a copy to ${user.email} so you can see exactly what your customer will see.` : "Your account has no email set"}
              data-testid="send-test-to-me-btn"
            >
              {sendingTest ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <FlaskConical className="w-5 h-5 mr-2" />}
              Test
            </Button>
            <EmailEngagementBadge data={emailEngagement} />
            <FollowupBadge
              data={followupStatus}
              isOpen={followupPopoverOpen}
              onOpenChange={setFollowupPopoverOpen}
              onStop={stopFollowup}
            />
            {isQuoteExpired(quote) && (
              <Button className="h-12 bg-amber-500 hover:bg-amber-600 text-white" onClick={async () => {
                try {
                  const res = await axios.post(`${API_URL}/quotes/${id}/reopen`);
                  toast.success(`Quote reopened — valid until ${new Date(res.data.valid_until + "T12:00:00").toLocaleDateString()}`);
                  setQuote(prev => ({ ...prev, valid_until: res.data.valid_until, status: prev.status === "expired" || prev.status === "rejected" ? (prev.sent_at ? "sent" : "draft") : prev.status }));
                } catch (e) { toast.error(e?.response?.data?.detail || "Failed to reopen"); }
              }} data-testid="reopen-quote-btn"><RotateCcw className="w-5 h-5 mr-2" />Reopen</Button>
            )}
            <Button variant="outline" className="h-12" onClick={async () => {
              try {
                const token = localStorage.getItem("token");
                const res = await axios.post(`${API_URL}/quotes/${id}/duplicate`, {}, { headers: { Authorization: `Bearer ${token}` } });
                toast.success("Quote duplicated!");
                navigate(`/quotes/${res.data.id}`);
              } catch { toast.error("Failed to duplicate"); }
            }} data-testid="duplicate-quote-btn"><Copy className="w-5 h-5 mr-2" />Duplicate</Button>
          </>
        )}
        {!isNew && <AutosaveStatus status={autosaveStatus} savedAt={savedAt} />}
        <Button className="bg-[#0F4C81] hover:bg-[#0D4270] h-12 px-6" onClick={handleSave} disabled={saving} data-testid="save-quote-btn">
          {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Save className="w-5 h-5 mr-2" />}Save
        </Button>
      </Header>

      <div className="p-4 sm:p-6">
        {/* Signed Quote Summary — shows once the customer signs.
            Displays both signatures, the agreed total, and every option/addon
            the customer picked so the admin doesn't have to open the customer
            view to see what was actually accepted. */}
        {quote.customer_signature?.signature && (
          <Card className="mb-6 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="quote-signed-summary">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center justify-between gap-2 text-base">
                <div className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-emerald-600" />
                  Signed &amp; Agreed
                </div>
                <span className="text-lg font-bold text-emerald-700">${(quote.approved_total ?? quote.total ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Signatures side by side */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="rounded-lg bg-white border border-emerald-100 p-3">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1">Customer</p>
                  {quote.customer_signature?.signature ? (
                    <img src={quote.customer_signature.signature} alt="Customer signature" className="max-h-14 mb-1" />
                  ) : (<p className="text-sm text-slate-400">Pending</p>)}
                  <p className="text-xs text-slate-600 font-medium">{quote.customer_signature?.name || contact?.name || "—"}</p>
                  {quote.customer_signature?.signed_at && (
                    <p className="text-[10px] text-slate-400">{new Date(quote.customer_signature.signed_at).toLocaleString()}</p>
                  )}
                </div>
                <div className="rounded-lg bg-white border border-emerald-100 p-3">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1">Company</p>
                  {quote.admin_signature?.signature ? (
                    <img src={quote.admin_signature.signature} alt="Company signature" className="max-h-14 mb-1" />
                  ) : (<p className="text-sm text-amber-600 font-medium">Awaiting counter-signature</p>)}
                  <p className="text-xs text-slate-600 font-medium">{quote.admin_signature?.name || "—"}</p>
                  {quote.admin_signature?.signed_at && (
                    <p className="text-[10px] text-slate-400">{new Date(quote.admin_signature.signed_at).toLocaleString()}</p>
                  )}
                </div>
              </div>
              {/* Signed Line Items — mirrors the customer's Signed Summary
                  panel on the public quote view. Prefers the persisted
                  `approved_breakdown` snapshot (exact numbers the customer
                  saw); falls back to walking the pages against the
                  `selected_options` array / `selected_addons` for older
                  quotes that predate the breakdown snapshot. */}
              {(() => {
                const breakdown = Array.isArray(quote.approved_breakdown) ? quote.approved_breakdown : [];
                const rows = [];
                if (breakdown.length > 0) {
                  breakdown.forEach(r => rows.push({
                    label: r.label || (r.kind === "addon" ? "Add-on" : "Option"),
                    amount: Number(r.amount || 0),
                    kind: (r.kind || "option").toLowerCase(),
                    mandatory: !!r.mandatory,
                  }));
                } else {
                  // Legacy fallback — walk pages against the selection arrays.
                  const selOptSet = new Set(
                    Array.isArray(quote.selected_options)
                      ? quote.selected_options
                      : (quote.selected_option ? [quote.selected_option] : [])
                  );
                  const selAddons = new Set(Array.isArray(quote.selected_addons) ? quote.selected_addons : []);
                  (quote.pages || []).forEach(page => {
                    const pt = page.page_type;
                    const c = page.content || {};
                    if (pt === "option" && selOptSet.has(page.id)) {
                      rows.push({
                        label: c.title || page.title || "Option",
                        amount: Number(c.base_price || c.manual_price || c.calculated_total || 0),
                        kind: "option",
                      });
                    } else if (pt === "addon" && (selAddons.has(page.id) || c.mandatory === true)) {
                      rows.push({
                        label: c.title || page.title || "Add-on",
                        amount: Number(c.base_price || c.manual_price || c.calculated_total || 0),
                        kind: "addon",
                        mandatory: c.mandatory === true,
                      });
                    }
                  });
                }
                if (rows.length === 0) return null;
                return (
                  <div className="rounded-lg bg-white border border-emerald-100 p-3" data-testid="signed-selections-block">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-2">Customer Selections ({rows.length})</p>
                    <div className="space-y-1.5">
                      {rows.map((r, i) => {
                        const isDiscount = r.kind === "discount";
                        const amtStr = isDiscount
                          ? `-$${Math.abs(r.amount).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : `$${r.amount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                        const badge = isDiscount ? "Discount" : r.kind === "addon" ? (r.mandatory ? "Add-on • Required" : "Add-on") : "Option";
                        return (
                          <div key={i} className="flex items-center justify-between text-sm" data-testid={`signed-selection-row-${i}`}>
                            <div className="min-w-0 pr-3">
                              <p className="text-[9px] uppercase tracking-wider font-semibold text-slate-400">{badge}</p>
                              <p className="text-slate-700 truncate">{r.label}</p>
                            </div>
                            <span className={`font-semibold whitespace-nowrap ${isDiscount ? "text-emerald-600" : "text-slate-700"}`}>{amtStr}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              <div className="flex flex-wrap gap-2">
                {!quote.admin_signature?.signature && (
                  <Button
                    size="sm"
                    className="bg-[#0F4C81] hover:bg-[#0D4270] text-white"
                    onClick={() => { setSignatureType("admin"); setShowSignatureModal(true); }}
                    data-testid="signed-summary-counter-sign-btn"
                  >
                    <PenTool className="w-4 h-4 mr-1" />Counter-Sign
                  </Button>
                )}
                {quote.admin_signature?.signature && (
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    onClick={async () => {
                      const email = contact?.email || "";
                      const target = window.prompt(`Re-send the fully countersigned PDF to the customer?\n\nRecipient (leave as-is to send to their saved email):`, email);
                      if (target === null) return;
                      try {
                        const res = await axios.post(`${API_URL}/quotes/${quote.id}/send-countersigned`, target && target.trim() !== email ? { recipient_email: target.trim() } : {});
                        toast.success(`Countersigned PDF sent to ${res.data?.recipient_email || email}`);
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Failed to send countersigned quote");
                      }
                    }}
                    data-testid="signed-summary-resend-countersigned-btn"
                    title="Re-email the countersigned PDF (in case the customer never got it)"
                  >
                    <Send className="w-4 h-4 mr-1" />Send Countersigned
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(`${window.location.origin}/q/${quote.short_code}`, "_blank")}
                  data-testid="signed-summary-open-btn"
                  disabled={!quote.short_code}
                >
                  <Eye className="w-4 h-4 mr-1" />Open signed quote
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadPDF}
                  data-testid="signed-summary-pdf-btn"
                >
                  <Download className="w-4 h-4 mr-1" />Signed PDF
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-blue-700 border-blue-300 hover:bg-blue-50 hover:text-blue-800"
                  onClick={async () => {
                    if (!window.confirm("Recompute this quote's total, approved total, and Customer Selections from the current pages? Use this if any of the numbers look stale.")) return;
                    try {
                      const res = await axios.post(`${API_URL}/admin/quote-heal/${quote.id}`);
                      toast.success(`Healed — total $${Number(res.data?.approved_total || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
                      window.location.reload();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Heal failed");
                    }
                  }}
                  data-testid="signed-summary-heal-btn"
                  title="Force-recompute total + selections from the quote pages"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />Recompute Totals
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-amber-700 border-amber-300 hover:bg-amber-50 hover:text-amber-800"
                  onClick={() => setShowUnsignConfirm(true)}
                  data-testid="signed-summary-unsign-btn"
                  title="Clear both signatures so you can edit and re-send this quote"
                >
                  <RotateCcw className="w-4 h-4 mr-1" />Undo Signing
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customer answers card — visible once the customer has signed and
            provided answers to the template's signing questions. Surfaces the
            install crew's prep info (color choices, drop-off side, notes, etc.)
            without diving into the public quote URL. */}
        {(() => {
          const answers = quote.customer_answers || {};
          const answerCount = Object.values(answers).filter(v => v !== null && v !== undefined && String(v).trim() !== "").length;
          if (!answerCount) return null;
          // Resolve the question definitions so we know each label/order.
          // Prefer the snapshot frozen at signing time — it survives later
          // template edits/deletions. Then the per-quote override, then the
          // live template.
          const snapshot = Array.isArray(quote.signing_questions_snapshot) ? quote.signing_questions_snapshot : [];
          const tplOverride = Array.isArray(quote.signing_questions_override) ? quote.signing_questions_override : [];
          let questions = snapshot.length ? snapshot : (tplOverride.length ? tplOverride : null);
          if (!questions) {
            const tpl = (quoteTemplates || []).find(t => t.id === quote.template_id);
            questions = (tpl && Array.isArray(tpl.signing_questions)) ? tpl.signing_questions : [];
          }
          // Sort by order (fallback: insertion order)
          const sorted = [...questions].sort((a, b) => (a.order || 0) - (b.order || 0));
          // Any stored answer with no matching question definition still gets
          // rendered (raw key as the label) so a signed answer is never
          // silently hidden from the crew.
          const knownIds = new Set(sorted.map(q => q.id));
          const orphans = Object.keys(answers)
            .filter(k => !knownIds.has(k))
            .map(k => ({ id: k, label: String(k).replace(/[_-]+/g, " ") }));
          const rows = [...sorted, ...orphans];
          return (
            <Card className="mb-6 border-emerald-200 bg-emerald-50/30" data-testid="quote-customer-answers-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Check className="w-5 h-5 text-emerald-600" />
                  Customer Answers ({answerCount})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {rows.map((q) => {
                    const v = answers[q.id];
                    if (v == null || (typeof v === "string" && !v.trim())) return null;
                    return (
                      <div key={q.id} className="rounded-lg bg-white border border-emerald-100 p-3" data-testid={`answer-${q.id}`}>
                        <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1">{q.label}</p>
                        <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{String(v)}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="details" className="h-12 px-6">Details</TabsTrigger>
            <TabsTrigger value="pages" className="h-12 px-6">Pages</TabsTrigger>
            <TabsTrigger value="pricing" className="h-12 px-6">Pricing</TabsTrigger>
            <TabsTrigger value="preview" className="h-12 px-6">Preview</TabsTrigger>
          </TabsList>

          {/* =================== DETAILS TAB =================== */}
          <TabsContent value="details">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader><CardTitle>Quote Information</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>Project *</Label>
                    <Select value={quote.project_id || "none"} onValueChange={(v) => v !== "none" && handleProjectSelect(v)}>
                      <SelectTrigger className="h-12" data-testid="quote-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Select project...</SelectItem>
                        {projects.map(p => <SelectItem key={p.id} value={p.id}>{contacts.find(c => c.id === p.contact_id)?.name} - {p.project_address || "No address"}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Template</Label>
                    <Select value={quote.template_id || "none"} onValueChange={(v) => v !== "none" && handleTemplateSelect(v)}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Apply template" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No template</SelectItem>
                        {quoteTemplates.map(t => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name} {t.is_default ? "(Default)" : ""} {t.service_type ? `- ${t.service_type}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400">Default templates auto-apply when you select a project with a matching service type</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Status</Label>
                      <Badge className={`px-4 py-2 text-sm ${quote.status === "draft" ? "bg-slate-100 text-slate-600" : quote.status === "sent" ? "bg-blue-100 text-blue-700" : quote.status === "approved" || quote.status === "signed" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100"}`}>
                        {quote.status || "draft"}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      <Label>Valid Until</Label>
                      <DatePickerField value={quote.valid_until || ""} onChange={(v) => setQuote(prev => ({ ...prev, valid_until: v }))} height="h-12" testId="quote-valid-until" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea value={quote.notes || ""} onChange={(e) => setQuote(prev => ({ ...prev, notes: e.target.value }))} placeholder="Internal notes..." rows={3} />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader><CardTitle>Customer Info</CardTitle></CardHeader>
                <CardContent>
                  {contact ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <h4 className="font-medium text-slate-800 mb-2">Contact</h4>
                        <p className="text-slate-700">{contact.name}</p>
                        {contact.phone && <p className="text-sm text-slate-500">{contact.phone}</p>}
                        {contact.email && <p className="text-sm text-slate-500">{contact.email}</p>}
                        {contact.address && <p className="text-sm text-slate-500">{contact.address}</p>}
                      </div>
                      {project && (
                        <div className="p-4 bg-slate-50 rounded-lg">
                          <h4 className="font-medium text-slate-800 mb-2">Project</h4>
                          <p className="text-slate-700">{project.project_address || "No address"}</p>
                          <p className="text-sm text-slate-500 capitalize">{project.service_type?.replace(/_/g, " ")}</p>
                          <Badge variant="outline" className="mt-2 capitalize">{project.classification}</Badge>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-center py-8 text-slate-500">Select a project to see customer info</p>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Per-quote signing questions override */}
            <div className="mt-6">
              {(() => {
                const override = Array.isArray(quote.signing_questions_override) ? quote.signing_questions_override : [];
                const overrideOn = override.length > 0;
                const tpl = (quoteTemplates || []).find(t => t.id === quote.template_id);
                const tplQuestions = (tpl && Array.isArray(tpl.signing_questions)) ? tpl.signing_questions : [];
                return (
                  <Card data-testid="quote-signing-questions-card">
                    <CardHeader className="pb-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div>
                          <CardTitle className="text-base">Customer Signing Questions</CardTitle>
                          <p className="text-xs text-slate-500 mt-1">
                            {overrideOn
                              ? "Customized for this quote (template's defaults are not used)."
                              : `Using template defaults (${tplQuestions.length} ${tplQuestions.length === 1 ? "question" : "questions"}).`}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          {!overrideOn && tplQuestions.length > 0 && (
                            <Button size="sm" variant="outline" onClick={() => setQuote(prev => ({ ...prev, signing_questions_override: tplQuestions.map(q => ({ ...q })) }))} data-testid="customize-questions-btn">
                              Customize for this quote
                            </Button>
                          )}
                          {!overrideOn && tplQuestions.length === 0 && (
                            <Button size="sm" variant="outline" onClick={() => setQuote(prev => ({ ...prev, signing_questions_override: [{ id: `q-${Date.now()}`, label: "New question", type: "text", required: false, order: 0, options: [], help_text: "", dynamic_links: [] }] }))}>
                              Add quote-specific question
                            </Button>
                          )}
                          {overrideOn && (
                            <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" onClick={() => setQuote(prev => ({ ...prev, signing_questions_override: [] }))} data-testid="reset-questions-btn">
                              Reset to template
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    {overrideOn && (
                      <CardContent>
                        <SigningQuestionsEditor
                          questions={override}
                          onChange={(qs) => setQuote(prev => ({ ...prev, signing_questions_override: qs }))}
                        />
                      </CardContent>
                    )}
                  </Card>
                );
              })()}
            </div>
          </TabsContent>

          {/* =================== PAGES TAB =================== */}
          <TabsContent value="pages">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Page List */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base">Pages</CardTitle>
                    <Button size="sm" className="bg-[#0F4C81] hover:bg-[#0D4270]" onClick={() => setShowPageModal(true)} data-testid="add-page-btn">
                      <Plus className="w-4 h-4 mr-1" />Add
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {quote.pages.length > 0 ? (
                    <DragDropContext onDragEnd={handleDragEndPages}>
                      <Droppable droppableId="quote-pages">
                        {(provided) => (
                          <div className="space-y-2" ref={provided.innerRef} {...provided.droppableProps}>
                            {quote.pages.map((page, index) => {
                              const pageConfig = PAGE_TYPES.find(p => p.value === page.page_type);
                              const PageIcon = pageConfig?.icon || FileText;
                              const isActive = selectedPage?.id === page.id;
                              let isNested = false;
                              if (page.page_type === "option" || page.page_type === "addon") {
                                for (let i = index - 1; i >= 0; i--) {
                                  if (quote.pages[i].page_type === "structure") { isNested = true; break; }
                                  if (quote.pages[i].page_type !== "option" && quote.pages[i].page_type !== "addon") break;
                                }
                              }
                              return (
                                <Draggable key={page.id} draggableId={page.id} index={index}>
                                  {(dragProvided, snapshot) => (
                                    <div
                                      ref={dragProvided.innerRef}
                                      {...dragProvided.draggableProps}
                                      style={dragProvided.draggableProps.style}
                                      className={`flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors ${snapshot.isDragging ? "shadow-lg ring-2 ring-[#0F4C81]/30" : ""} ${isNested ? "ml-5 border-l-4 border-indigo-200" : ""} ${isActive ? "bg-[#0F4C81]/10 border border-[#0F4C81]/30" : "bg-slate-50 hover:bg-slate-100"} ${page.enabled === false ? "opacity-40" : ""}`}
                                      onClick={() => setSelectedPage(page)}
                                      data-testid={`page-item-${page.page_type}`}
                                    >
                                      <div {...dragProvided.dragHandleProps} className="cursor-grab active:cursor-grabbing flex-shrink-0 text-slate-300 hover:text-slate-500" onClick={e => e.stopPropagation()}>
                                        <GripVertical className="w-4 h-4" />
                                      </div>
                                      <Switch
                                        checked={page.enabled !== false}
                                        onCheckedChange={(v) => { setQuote(prev => ({ ...prev, pages: prev.pages.map(p => p.id === page.id ? { ...p, enabled: v } : p) })); }}
                                        onClick={(e) => e.stopPropagation()}
                                        className="scale-[0.65] flex-shrink-0"
                                        data-testid={`page-toggle-${page.page_type}`}
                                      />
                                      <PageIcon className="w-4 h-4 text-[#0F4C81] flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-700 truncate">{page.title}</p>
                                        <p className="text-xs text-slate-400">{pageConfig?.description}</p>
                                      </div>
                                      <Badge variant="outline" className="text-xs flex-shrink-0">{index + 1}</Badge>
                                      <Button variant="ghost" size="sm" className="text-slate-500 hover:text-[#0F4C81] h-7 w-7 p-0 flex-shrink-0" title="Duplicate this page" onClick={(e) => { e.stopPropagation(); handleDuplicatePage(page.id); }} data-testid={`page-duplicate-${page.page_type}`}>
                                        <Copy className="w-3 h-3" />
                                      </Button>
                                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600 h-7 w-7 p-0 flex-shrink-0" onClick={(e) => { e.stopPropagation(); handleRemovePage(page.id); }}>
                                        <Trash2 className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  )}
                                </Draggable>
                              );
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-500 mb-3">No pages yet</p>
                      <Button size="sm" onClick={() => setShowPageModal(true)}><Plus className="w-4 h-4 mr-1" />Add Page</Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Page Editor */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-base">{selectedPage ? `Edit: ${selectedPage.title}` : "Select a page to edit"}</CardTitle>
                </CardHeader>
                <CardContent>
                  {selectedPage ? (
                    <div>
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-3 mb-4">
                        <div className="space-y-2">
                          <Label>Page Title</Label>
                          <Input
                            value={selectedPage.title || ""}
                            onChange={(e) => {
                              const newTitle = e.target.value;
                              setQuote(prev => ({ ...prev, pages: prev.pages.map(p => p.id === selectedPage.id ? { ...p, title: newTitle } : p) }));
                              setSelectedPage(prev => ({ ...prev, title: newTitle }));
                            }}
                            className="h-12"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Page Type</Label>
                          <Select
                            value={selectedPage.page_type || ""}
                            onValueChange={(newType) => {
                              // Convert the page to a new type. Existing content
                              // is preserved on the page object — each editor
                              // only reads keys it understands, so switching is
                              // safe and reversible.
                              setQuote(prev => ({ ...prev, pages: prev.pages.map(p => p.id === selectedPage.id ? { ...p, page_type: newType } : p) }));
                              setSelectedPage(prev => ({ ...prev, page_type: newType }));
                            }}
                          >
                            <SelectTrigger className="h-12" data-testid="page-type-picker">
                              <SelectValue placeholder="Choose page type" />
                            </SelectTrigger>
                            <SelectContent>
                              {PAGE_TYPES.map(pt => (
                                <SelectItem key={pt.value} value={pt.value}>{pt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {(() => {
                        // Fall back to the Custom page editor when the page
                        // has an unknown / legacy page_type so the user can
                        // always add photos / videos / text / HTML.
                        const knownTypes = new Set(["cover", "text", "custom", "structure", "pricing", "signature", "financing", "option", "addon"]);
                        if (selectedPage.page_type && !knownTypes.has(selectedPage.page_type)) {
                          return (
                            <div className="space-y-2">
                              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                                This page has an unrecognized type ("{selectedPage.page_type}"). Showing the <b>Custom</b> editor so you can add content. Use the Page Type picker above to set a permanent type.
                              </p>
                              <CustomPageEditor
                                page={selectedPage}
                                onChange={(content) => { handleUpdatePageContent(selectedPage.id, content); setSelectedPage(prev => ({ ...prev, content: { ...prev.content, ...content } })); }}
                              />
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {selectedPage.page_type === "cover" && (
                        <CoverPageEditor
                          page={selectedPage}
                          onChange={(content) => { handleUpdatePageContent(selectedPage.id, content); setSelectedPage(prev => ({ ...prev, content: { ...prev.content, ...content } })); }}
                          contact={contact}
                          project={project}
                          settings={quoteSettings}
                        />
                      )}
                      {selectedPage.page_type === "text" && (
                        <TextPageEditor
                          page={selectedPage}
                          onChange={(content) => { handleUpdatePageContent(selectedPage.id, content); setSelectedPage(prev => ({ ...prev, content: { ...prev.content, ...content } })); }}
                        />
                      )}
                      {selectedPage.page_type === "custom" && (
                        <CustomPageEditor
                          page={selectedPage}
                          onChange={(content) => { handleUpdatePageContent(selectedPage.id, content); setSelectedPage(prev => ({ ...prev, content: { ...prev.content, ...content } })); }}
                        />
                      )}
                      {selectedPage.page_type === "structure" && (
                        <StructurePageEditor
                          page={selectedPage}
                          priceList={priceList}
                          onChange={(content) => { handleUpdatePageContent(selectedPage.id, content); setSelectedPage(prev => ({ ...prev, content: { ...prev.content, ...content } })); }}
                        />
                      )}
                      {selectedPage.page_type === "pricing" && (
                        <div className="text-center py-8 text-slate-500">
                          <DollarSign className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                          <p>Pricing is managed in the <button className="text-[#0F4C81] underline" onClick={() => setActiveTab("pricing")}>Pricing tab</button></p>
                        </div>
                      )}
                      {selectedPage.page_type === "signature" && (
                        <div className="text-center py-8 text-slate-500">
                          <PenTool className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                          <p>Signatures are handled in the <button className="text-[#0F4C81] underline" onClick={() => { setPreviewDevice("desktop"); setShowCustomerPreview(true); }}>View & Sign</button> overlay</p>
                        </div>
                      )}
                      {selectedPage.page_type === "financing" && (
                        <FinancingPageEditor
                          page={selectedPage}
                          onChange={(content) => { handleUpdatePageContent(selectedPage.id, content); setSelectedPage(prev => ({ ...prev, content: { ...prev.content, ...content } })); }}
                        />
                      )}
                      {selectedPage.page_type === "option" && (() => {
                        // Collect line items from ALL structure pages on this quote (consistent
                        // with Preview, Pricing tab, and Customer View). Previously we only walked
                        // BACK to the nearest preceding structure, which missed items in quotes
                        // where the structure page is ordered after the option.
                        const structItems = (quote.pages || [])
                          .filter(p => p.page_type === "structure" && p.enabled !== false)
                          .flatMap(p => p.content?.line_items || []);
                        return (
                          <OptionPageEditor
                            page={selectedPage}
                            priceList={priceList}
                            structureLineItems={structItems}
                            profitMarginPct={quote.profit_margin_pct}
                            vendorTaxPct={quote.vendor_tax_pct}
                            onChange={(content) => { handleUpdatePageContent(selectedPage.id, content); setSelectedPage(prev => ({ ...prev, content: { ...prev.content, ...content } })); }}
                          />
                        );
                      })()}
                      {selectedPage.page_type === "addon" && (
                        <AddonPageEditor
                          page={selectedPage}
                          priceList={priceList}
                          onChange={(content) => { handleUpdatePageContent(selectedPage.id, content); setSelectedPage(prev => ({ ...prev, content: { ...prev.content, ...content } })); }}
                        />
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-16 text-slate-400">
                      <Eye className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                      <p>Click a page from the list to edit its content</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* ============= LIVE PREVIEW ============= */}
              {/*
                Renders the currently-selected page using the same component
                the customer would see (PagePreviewRenderer). Auto-refreshes
                on every keystroke since it just reads from `quote` state.
                Sits under the editor on tablet/desktop, stacks below it on
                mobile (grid drops to 1 col at that breakpoint). Users can
                collapse it if they want more editor real estate — choice
                is persisted in localStorage.
              */}
              {selectedPage && (
                <Card className="lg:col-span-3 mt-2 border-[#0F4C81]/20" data-testid="quote-builder-live-preview">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          <Eye className="w-4 h-4 text-[#0F4C81]" />
                          Live Preview — What Your Customer Sees
                        </CardTitle>
                        <p className="text-xs text-slate-400 mt-1">Updates as you edit. This is exactly how the selected page renders on the customer&apos;s View & Sign screen.</p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => {
                          const next = !showLivePreview;
                          setShowLivePreview(next);
                          try { localStorage.setItem("quote_builder_live_preview", next ? "on" : "off"); } catch { /* ignore */ }
                        }}
                        data-testid="live-preview-toggle-btn"
                      >
                        {showLivePreview ? "Hide" : "Show"}
                      </Button>
                    </div>
                  </CardHeader>
                  {showLivePreview && (
                    <CardContent>
                      <div className="border border-slate-200 rounded-lg bg-white p-4 sm:p-6 shadow-inner">
                        <SectionErrorBoundary label={`Live Preview (${selectedPage.page_type})`}>
                          <PagePreviewRenderer
                            page={selectedPage}
                            allPages={quote.pages || []}
                            settings={quoteSettings}
                            quote={quote}
                          />
                        </SectionErrorBoundary>
                      </div>
                    </CardContent>
                  )}
                </Card>
              )}
            </div>
          </TabsContent>

          {/* =================== PRICING TAB =================== */}
          <TabsContent value="pricing">
            <SectionErrorBoundary label="Pricing Tab">
            {/* Compute effective margin — back-calculated from the quote's
                actual total minus its total raw cost. When any option or
                addon has a manual "Set Price", the profit_margin_pct input
                stops reflecting reality (the manual price bypasses the
                margin formula). Showing the effective % here lets the
                admin see what margin they're actually taking on this quote
                without doing the math in their head. */}
            {/* Global Pricing Controls — like ProLine's overview */}
            <Card className="mb-6 border-slate-300">
              <CardContent className="p-4">
                {(() => {
                  // Sum every line-item cost across every enabled page.
                  const pages = quote.pages || [];
                  const rawSum = (p) => (p.content?.line_items || [])
                    .filter(it => it.visible !== false)
                    .reduce((s, it) => {
                      const q = parseFloat(it.quantity) || 0;
                      const pr = parseFloat(it.customer_price || it.unit_price) || 0;
                      const w = parseFloat(it.waste_factor) || 0;
                      // Raw cost (NOT customer price) — exclude margin markup
                      // so we compute true gross margin against real cost.
                      return s + q * (1 + w / 100) * pr;
                    }, 0);
                  const structureRawCost = pages
                    .filter(p => p.page_type === "structure" && p.enabled !== false)
                    .reduce((s, p) => s + rawSum(p), 0);
                  const activeOption = pages
                    .filter(p => p.page_type === "option" && p.enabled !== false)[0];
                  const optionRawCost = activeOption ? rawSum(activeOption) : 0;
                  const addonRawCost = pages
                    .filter(p => p.page_type === "addon" && p.enabled !== false)
                    .reduce((s, p) => s + rawSum(p), 0);
                  const totalRawCost = structureRawCost + optionRawCost + addonRawCost;
                  const customerTotal = parseFloat(quote.total) || 0;
                  const hasManualPrice = pages.some(p =>
                    (p.page_type === "option" || p.page_type === "addon")
                    && (parseFloat(p.content?.base_price || p.content?.manual_price) || 0) > 0
                  );
                  const effectiveMarginPct = customerTotal > 0
                    ? Math.round(((customerTotal - totalRawCost) / customerTotal) * 100 * 10) / 10
                    : 0;
                  // Stash on window so the JSX below can read without a full refactor.
                  window.__tidesEffMargin = { hasManualPrice, effectiveMarginPct, totalRawCost, customerTotal };
                  return null;
                })()}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    {(window.__tidesEffMargin?.hasManualPrice) ? (
                      // Manual price is active → show back-calculated effective
                      // margin as a READ-ONLY display so the admin sees the
                      // real margin their manual price implies. The regular
                      // profit_margin_pct input is bypassed by the pricing
                      // formula whenever a manual "Set Price" is present.
                      <div className="flex flex-col items-center" data-testid="global-effective-margin">
                        <div className="text-2xl font-bold text-emerald-600 tabular-nums">
                          {window.__tidesEffMargin.effectiveMarginPct}%
                        </div>
                        <p className="text-[10px] text-amber-700 uppercase tracking-wider mt-1 font-semibold">
                          Effective Margin
                        </p>
                        <p className="text-[10px] text-slate-400 mt-0.5">
                          (from manual price)
                        </p>
                      </div>
                    ) : (
                      <input
                      type="number" step="1" min="0" max="100"
                      value={quote.profit_margin_pct ?? ""}
                      onChange={e => {
                        const val = e.target.value === "" ? 0 : parseFloat(e.target.value);
                        setQuote(prev => {
                          const updated = { ...prev, profit_margin_pct: val };
                          // Recalculate total
                          const pages = updated.pages || [];
                          const calcRaw = (p) => {
                            const manual = parseFloat(p.content?.base_price || p.content?.manual_price) || 0;
                            if (manual > 0) return manual;
                            return (p.content?.line_items || []).reduce((sum, it) => {
                              const q = parseFloat(it.quantity) || 0;
                              const pr = parseFloat(it.customer_price || it.unit_price) || 0;
                              const m = parseFloat(it.margin) || 0;
                              const w = parseFloat(it.waste_factor) || 0;
                              return sum + q * (1 + w / 100) * pr * (1 + m / 100);
                            }, 0);
                          };
                          const structureRaw = pages.filter(p => p.page_type === "structure").reduce((s, p) => s + calcRaw(p), 0);
                          const firstOptRaw = pages.filter(p => p.page_type === "option").map(p => calcRaw(p))[0] || 0;
                          const addonRaw = pages.filter(p => p.page_type === "addon").reduce((s, p) => s + calcRaw(p), 0);
                          const totalRaw = structureRaw + firstOptRaw + addonRaw;
                          const taxPct = parseFloat(updated.vendor_tax_pct) || 0;
                          const mode = updated.pricing_mode === "markup" ? "markup" : "margin";
                          const withMargin = applyProfit(totalRaw, val, mode);
                          const tax = totalRaw * (taxPct / 100);
                          const mkp = parseFloat(updated.price_markup_pct) || 0;
                          const adj = parseFloat(updated.price_adjustment) || 0;
                          const sub = withMargin + tax;
                          updated.total = Math.round((sub + sub * mkp / 100 + adj) * 100) / 100;
                          return updated;
                        });
                      }}
                      className="w-20 h-10 text-2xl font-bold text-center text-emerald-600 bg-transparent border-none outline-none mx-auto block"
                      data-testid="global-profit-margin"
                    />
                    )}
                    {!(window.__tidesEffMargin?.hasManualPrice) && (
                      <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">
                      {quote.pricing_mode === "markup" ? "Markup %" : "Profit Margin %"}
                    </p>
                    )}
                    {/* If any option/addon has a manual Set Price, global margin
                        silently skips them. Give the admin a one-click escape
                        hatch so their margin actually applies everywhere. */}
                    {(quote.pages || []).some(p =>
                      (p.page_type === "option" || p.page_type === "addon")
                      && (parseFloat(p.content?.base_price || p.content?.manual_price) || 0) > 0
                    ) && (
                      <button
                        type="button"
                        onClick={() => {
                          setQuote(prev => ({
                            ...prev,
                            pages: (prev.pages || []).map(p => {
                              if (p.page_type !== "option" && p.page_type !== "addon") return p;
                              if (!p.content) return p;
                              return { ...p, content: { ...p.content, base_price: 0, manual_price: 0 } };
                            }),
                          }));
                          toast.success("Cleared Set Prices — margin now applies to every option");
                        }}
                        className="mt-2 text-[10px] font-semibold text-amber-700 underline hover:text-amber-800"
                        title="Some options have a manual Set Price that overrides margin. Click to clear them all."
                        data-testid="apply-margin-to-all"
                      >Apply margin to all options →</button>
                    )}
                    {/* Markup vs Margin toggle — flips the profit math */}
                    <div className="mt-2 inline-flex items-center bg-white border border-slate-200 rounded-md p-0.5">
                      <button
                        type="button"
                        onClick={() => setQuote(prev => ({ ...prev, pricing_mode: "margin" }))}
                        className={`px-2 py-0.5 text-[10px] font-semibold rounded transition-colors ${(quote.pricing_mode || "margin") === "margin" ? "bg-emerald-600 text-white" : "text-slate-500 hover:text-slate-700"}`}
                        title="cost / (1 - %). Industry-standard true gross margin."
                        data-testid="pricing-mode-margin"
                      >
                        Margin
                      </button>
                      <button
                        type="button"
                        onClick={() => setQuote(prev => ({ ...prev, pricing_mode: "markup" }))}
                        className={`px-2 py-0.5 text-[10px] font-semibold rounded transition-colors ${quote.pricing_mode === "markup" ? "bg-emerald-600 text-white" : "text-slate-500 hover:text-slate-700"}`}
                        title="cost * (1 + %). Simple 'add on top of cost' math."
                        data-testid="pricing-mode-markup"
                      >
                        Markup
                      </button>
                    </div>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <input
                      type="number" step="0.5" min="0" max="20"
                      value={quote.vendor_tax_pct ?? 0}
                      onChange={e => {
                        const val = e.target.value === "" ? 0 : parseFloat(e.target.value);
                        setQuote(prev => ({ ...prev, vendor_tax_pct: val }));
                      }}
                      className="w-20 h-10 text-2xl font-bold text-center text-emerald-600 bg-transparent border-none outline-none mx-auto block"
                      data-testid="global-vendor-tax"
                    />
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Vendor Tax %</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#0F4C81]">{quote.pages?.filter(p => p.page_type === "option").length || 0}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Options</p>
                  </div>
                  <div className="text-center p-3 bg-slate-50 rounded-lg">
                    <p className="text-2xl font-bold text-[#0F4C81]">{quote.pages?.filter(p => p.page_type === "addon").length || 0}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Add-ons</p>
                  </div>
                </div>
                {/* Quote-level discounts — multi-discount support.
                    Falls back to legacy single discount_amount/discount_label
                    on first render so existing quotes keep working. */}
                <div className="mt-4 bg-rose-50 border border-rose-200 rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <label className="text-[11px] font-bold uppercase tracking-widest text-rose-700">Quote Discounts</label>
                    <button
                      type="button"
                      onClick={() => {
                        const existing = Array.isArray(quote.discounts) ? quote.discounts : [];
                        // Migrate legacy single-discount into the list when the array is empty
                        const migrated = existing.length ? existing : (
                          parseFloat(quote.discount_amount) > 0
                            ? [{ id: `disc-${Date.now()}`, label: quote.discount_label || "Discount", type: "flat", value: parseFloat(quote.discount_amount) }]
                            : []
                        );
                        setQuote(prev => ({
                          ...prev,
                          discounts: [...migrated, { id: `disc-${Date.now()}-${Math.random().toString(36).slice(2,6)}`, label: "", type: "flat", value: 0 }],
                          discount_amount: 0,
                          discount_label: "",
                        }));
                      }}
                      className="text-[11px] font-bold uppercase tracking-wider text-rose-700 hover:text-rose-900 flex items-center gap-1"
                      data-testid="add-discount-btn"
                    >
                      + Add Discount
                    </button>
                  </div>
                  {/* Legacy discount migration shim — show one row if discounts[] is empty but legacy values exist */}
                  {(() => {
                    const discounts = Array.isArray(quote.discounts) ? quote.discounts : [];
                    const hasLegacy = !discounts.length && (parseFloat(quote.discount_amount) > 0 || quote.discount_label);
                    const rows = discounts.length ? discounts : (hasLegacy ? [{ id: "legacy", label: quote.discount_label || "Discount", type: "flat", value: parseFloat(quote.discount_amount) || 0, _legacy: true }] : []);
                    if (!rows.length) {
                      return <p className="text-[11px] text-rose-700/70 italic">No discounts. Add one — flat $ or %. Applies to the customer's option subtotal + add-ons. Reflected on every page, preview, pricing tab, and customer view.</p>;
                    }
                    const persist = (next) => {
                      // Strip any legacy marker before saving
                      const clean = next.map(({ _legacy, ...rest }) => rest);
                      setQuote(prev => ({ ...prev, discounts: clean, discount_amount: 0, discount_label: "" }));
                    };
                    return rows.map((d, idx) => (
                      <div key={d.id || idx} className="flex items-center gap-2 flex-wrap">
                        <input
                          value={d.label || ""}
                          onChange={(e) => persist(rows.map((r, i) => i === idx ? { ...r, label: e.target.value } : r))}
                          placeholder="Reason (e.g., Chart, Veteran, Spring promo)"
                          className="flex-1 min-w-[160px] h-8 px-2 text-sm rounded border border-rose-200 bg-white focus:outline-none focus:ring-1 focus:ring-rose-300"
                          data-testid={`discount-label-${idx}`}
                        />
                        <select
                          value={d.type || "flat"}
                          onChange={(e) => persist(rows.map((r, i) => i === idx ? { ...r, type: e.target.value } : r))}
                          className="h-8 px-2 text-sm rounded border border-rose-200 bg-white focus:outline-none focus:ring-1 focus:ring-rose-300"
                          data-testid={`discount-type-${idx}`}
                        >
                          <option value="flat">$ Flat</option>
                          <option value="percent">% Percent</option>
                        </select>
                        <div className="relative w-28">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-rose-400">{d.type === "percent" ? "%" : "−$"}</span>
                          <input
                            type="number"
                            step={d.type === "percent" ? "0.1" : "1"}
                            value={d.value ?? ""}
                            onChange={(e) => persist(rows.map((r, i) => i === idx ? { ...r, value: e.target.value === "" ? 0 : parseFloat(e.target.value) } : r))}
                            placeholder="0"
                            className="w-full h-8 pl-7 pr-2 text-sm font-bold text-rose-700 rounded border border-rose-200 bg-white focus:outline-none focus:ring-1 focus:ring-rose-300"
                            data-testid={`discount-value-${idx}`}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => persist(rows.filter((_, i) => i !== idx))}
                          className="h-8 w-8 flex items-center justify-center rounded text-rose-500 hover:text-rose-700 hover:bg-rose-100"
                          data-testid={`remove-discount-${idx}`}
                          aria-label="Remove discount"
                        >×</button>
                      </div>
                    ));
                  })()}
                  <p className="text-[10px] text-rose-700/70 mt-1.5">Discounts apply in order. Flat $ or %. Reflected everywhere — pages, previews, pricing tab, customer View &amp; Sign, and PDF.</p>
                </div>
              </CardContent>
            </Card>
            <PricingTab
              hasPagePricing={hasPagePricing}
              pricingPages={pricingPages}
              quote={quote}
              priceList={priceList}
              showBudget={false}
              updatePageLineItem={updatePageLineItem}
              togglePageCashDiscount={togglePageCashDiscount}
              updatePageTaxRate={updatePageTaxRate}
              onPriceAdjustmentChange={handlePriceAdjustmentChange}
              onSetPagePrice={handleSetPagePrice}
              onResetMarginsToPriceList={resetPageMarginsToPriceList}
              baseItems={baseItems}
              addonItems={addonItems}
              selectedAddons={selectedAddons}
              toggleAddon={toggleAddon}
              handleRemoveLineItem={handleRemoveLineItem}
              setNewLineItem={setNewLineItem}
              setShowLineItemModal={setShowLineItemModal}
              markupSettings={markupSettings}
              setMarkupSettings={setMarkupSettings}
              showMarkupSettings={showMarkupSettings}
              setShowMarkupSettings={setShowMarkupSettings}
              handleCalculate={handleCalculate}
              isNew={isNew}
            />
            </SectionErrorBoundary>
          </TabsContent>

          {/* =================== PREVIEW TAB =================== */}
          <TabsContent value="preview">
            <SectionErrorBoundary label="Preview Tab">
            <Card>
              <CardHeader><CardTitle>Quote Preview</CardTitle></CardHeader>
              <CardContent>
                <div className="max-w-2xl mx-auto space-y-8">
                  {/* Cover Page */}
                  {(() => {
                    const coverPage = quote.pages.find(p => p.page_type === "cover");
                    const cc = coverPage?.content || {};
                    return (
                      <CoverPageDisplay
                        headline={cc.headline || "PROPOSAL"}
                        subheadline={cc.subheadline || ""}
                        logoUrl={cc.logo_url || ""}
                        bgImageUrl={cc.bg_image_url || ""}
                        bgColor={cc.bgColor || "#0F4C81"}
                        companyName={quoteSettings?.company_name || ""}
                        companyPhone={quoteSettings?.company_phone || ""}
                        companyEmail={quoteSettings?.company_email || ""}
                        companyAddress={quoteSettings?.company_address || ""}
                        contactName={contact?.name || ""}
                        projectAddress={project?.project_address || ""}
                        quoteNumber={quote.quote_number ? `Quote #${quote.quote_number}` : ""}
                        showLogo={cc.show_logo !== false}
                        showDate={cc.show_date !== false}
                        showQuoteNumber={cc.show_quote_number !== false}
                        showContactInfo={cc.show_contact_info !== false}
                        showCompanyInfo={cc.show_company_info !== false}
                      />
                    );
                  })()}
                  {/* Pages content preview */}
                  {quote.pages.filter(p => p.enabled !== false).map(page => {
                    const pt = page.page_type;
                    const c = page.content || {};
                    if (pt === "cover") return null; // already shown above
                    if (pt === "structure") {
                      if (!c.notes || c.notes === "<p></p>") return null;
                      return (
                        <div key={page.id} className="bg-gradient-to-r from-[#0F4C81] to-[#0F4C81]/90 rounded-xl p-5 mt-2" data-testid="preview-structure-notes">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-white font-bold text-lg">{c.title || page.title}</span>
                          </div>
                          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                            <RichTextContent content={c.notes} className="text-white/90 text-sm leading-relaxed [&_p]:text-white/90" />
                          </div>
                        </div>
                      );
                    }
                    if (pt === "text") return (
                      <div key={page.id} className="mt-8 pt-6 border-t-2 border-slate-200">
                        <h3 className="text-lg font-semibold text-slate-700 mb-3">{c.title || page.title}</h3>
                        {c.body && (
                          typeof c.body === "string" && c.body.startsWith("<") 
                            ? <RichTextContent content={c.body} className="text-slate-600 text-base leading-relaxed" />
                            : typeof c.body === "string" ? <p className="text-slate-600 text-base whitespace-pre-wrap leading-relaxed">{c.body}</p>
                            : <RichTextContent content={c.body} className="text-slate-600 text-base leading-relaxed" />
                        )}
                        {c.sections?.map((s, si) => (
                          <div key={si} className="mt-4">
                            {s.title && <p className="font-semibold text-slate-700 text-base mb-1">{s.title}</p>}
                            {s.body && (
                              typeof s.body === "string" && s.body.startsWith("<")
                                ? <RichTextContent content={s.body} className="text-slate-600 text-base leading-relaxed" />
                                : typeof s.body === "string" ? <p className="text-slate-600 text-base whitespace-pre-wrap leading-relaxed">{s.body}</p>
                                : <RichTextContent content={s.body} className="text-slate-600 text-base leading-relaxed" />
                            )}
                          </div>
                        ))}
                      </div>
                    );
                    if (pt === "option") return (
                      <div key={page.id} className="mt-8 border-2 border-[#0F4C81]/20 rounded-xl overflow-hidden">
                        <div className="bg-[#0F4C81] px-5 py-2 flex items-center gap-2">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-white/70">Option</span>
                          {c.option_tag && <Badge className="bg-white/20 text-white text-[10px] border-0">{c.option_tag}</Badge>}
                        </div>
                        <div className="p-5 bg-white">
                          <h3 className="text-xl font-bold text-[#0F4C81] mb-2">{c.option_name || page.title}</h3>
                        {c.description && <RichTextContent content={c.description} className="text-slate-600 mb-3" />}
                        {c.scope && <div className="mb-3"><p className="font-medium text-slate-700 text-sm">Scope of Work:</p><RichTextContent content={c.scope} className="text-slate-600 text-sm" /></div>}
                        {c.important_info && (
                          <div className="mb-3 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="preview-important-info">
                            <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-1">Important Information</p>
                            <RichTextContent content={c.important_info} className="text-sm text-amber-800 leading-relaxed" />
                          </div>
                        )}
                        {/* Show total price — option + structure + vendor tax + profit margin */}
                        {c.show_price !== false && (() => {
                          const price = calcFullOptionPrice(page);
                          // --- Admin-only profit breakdown (hidden on customer view) ---
                          const items = c.line_items || [];
                          const optionItemsCost = items.reduce((sum, it) => {
                            const q = parseFloat(it.quantity) || 0;
                            const w = parseFloat(it.waste_factor) || 0;
                            const up = parseFloat(it.unit_price) || 0;
                            return sum + q * (1 + w / 100) * up;
                          }, 0);
                          // Include shared cost from structure pages (every option carries this)
                          const structureItemsCost = (quote.pages || [])
                            .filter(p => p.page_type === "structure" && p.enabled !== false)
                            .reduce((sum, sp) => {
                              const sItems = (sp.content?.line_items || []).filter(it => it.visible !== false);
                              return sum + sItems.reduce((s, it) => {
                                const q = parseFloat(it.quantity) || 0;
                                const w = parseFloat(it.waste_factor) || 0;
                                const up = parseFloat(it.unit_price) || 0;
                                return s + q * (1 + w / 100) * up;
                              }, 0);
                            }, 0);
                          // Selected add-ons — fold in both cost and revenue so the
                          // profit panel reflects what the customer *actually* buys
                          // once they've ticked upgrade boxes.
                          const selectedAddonPages = (quote.pages || [])
                            .filter(p => p.page_type === "addon" && p.enabled !== false && selectedAddons[p.id]);
                          const addonPagesCost = selectedAddonPages.reduce((s, p) => s + calcAddonPageCost(p), 0);
                          const addonPagesRevenue = selectedAddonPages.reduce((s, p) => s + calcAddonPageRevenue(p), 0);
                          // Line-item level add-ons on the quote itself
                          const selectedItemAddons = (quote.line_items || [])
                            .filter(it => it.is_addon && selectedAddons[it.id]);
                          const itemAddonsCost = selectedItemAddons.reduce((s, it) => {
                            const q = parseFloat(it.quantity) || 0;
                            const w = parseFloat(it.waste_factor) || 0;
                            const up = parseFloat(it.unit_price) || 0;
                            return s + q * (1 + w / 100) * up;
                          }, 0);
                          const itemAddonsRevenue = selectedItemAddons.reduce((s, it) => s + calcItemTotal(it), 0);
                          const totalAddonsCost = addonPagesCost + itemAddonsCost;
                          const totalAddonsRevenue = addonPagesRevenue + itemAddonsRevenue;
                          const rawItemsCost = optionItemsCost + structureItemsCost + totalAddonsCost;
                          const manualCost = parseFloat(c.cost_override) || 0;
                          const yourCost = (manualCost > 0 ? manualCost : rawItemsCost);
                          const effectivePrice = price + totalAddonsRevenue;
                          const margin$ = effectivePrice - yourCost;
                          const marginPct = effectivePrice > 0 ? (margin$ / effectivePrice * 100) : 0;
                          const marginColor = marginPct >= 30 ? "text-emerald-600" : marginPct >= 15 ? "text-amber-600" : "text-red-600";
                          const addonsSelectedCount = selectedAddonPages.length + selectedItemAddons.length;
                          return price > 0 ? (
                            <div className="mt-4 bg-[#0F4C81]/5 border border-[#0F4C81]/20 rounded-lg p-4">
                              {/* Admin profit panel */}
                              <div className="mb-3 pb-3 border-b border-dashed border-slate-300 bg-amber-50/70 -mx-4 -mt-4 px-4 pt-3 rounded-t-lg" data-testid={`option-profit-panel-${page.id}`}>
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Private Profit Breakdown</span>
                                  <span className="text-[9px] text-amber-700/70 italic">Hidden from customer</span>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                  <div>
                                    <label className="text-[10px] text-slate-500 uppercase tracking-wide">Your Cost</label>
                                    <input
                                      type="number"
                                      step="1"
                                      value={c.cost_override ?? ""}
                                      onChange={(e) => updatePageContent(page.id, { ...c, cost_override: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                                      placeholder={rawItemsCost.toFixed(2)}
                                      className="w-full mt-0.5 h-8 rounded border border-slate-200 bg-white px-2 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-amber-400"
                                      title={rawItemsCost > 0 ? `Auto-calc (option $${optionItemsCost.toFixed(2)} + shared $${structureItemsCost.toFixed(2)} = $${rawItemsCost.toFixed(2)}). Type to override.` : "Enter your actual cost"}
                                      data-testid={`option-cost-override-${page.id}`}
                                    />
                                  </div>
                                  <div>
                                    <label className="text-[10px] text-slate-500 uppercase tracking-wide">Sells For</label>
                                    <input
                                      type="number"
                                      step="1"
                                      value={c.base_price ?? c.manual_price ?? ""}
                                      onChange={(e) => updatePageContent(page.id, { ...c, base_price: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                                      placeholder={price.toFixed(2)}
                                      className="w-full mt-0.5 h-8 rounded border border-[#0F4C81]/30 bg-white px-2 text-sm font-bold text-[#0F4C81] focus:outline-none focus:ring-1 focus:ring-[#0F4C81]"
                                      title="The price shown to your customer. Type to override — flows to Investment, Pages tab, Pricing tab, and customer view."
                                      data-testid={`option-sells-for-${page.id}`}
                                    />
                                  </div>
                                  <div className="col-span-2 sm:col-span-1">
                                    <label className="text-[10px] text-slate-500 uppercase tracking-wide">Margin</label>
                                    <div className={`mt-0.5 h-8 flex items-center justify-between gap-2 px-2 rounded bg-white border border-slate-200 text-xs font-bold ${marginColor}`}>
                                      <span className="whitespace-nowrap">${margin$.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</span>
                                      <span className="whitespace-nowrap">{marginPct.toFixed(1)}%</span>
                                    </div>
                                  </div>
                                </div>
                                {addonsSelectedCount > 0 && (
                                  <div className="mt-2 pt-2 border-t border-dashed border-amber-300/60 text-[11px] text-amber-900/80 flex items-center justify-between gap-2 flex-wrap" data-testid={`option-addons-included-${page.id}`}>
                                    <span>Includes <b>{addonsSelectedCount}</b> selected add-on{addonsSelectedCount > 1 ? "s" : ""}: <b>+${totalAddonsRevenue.toFixed(0)}</b> revenue / <b>+${totalAddonsCost.toFixed(0)}</b> cost</span>
                                    <span className="text-amber-700/70">Sold total: <b>${effectivePrice.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</b></span>
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center justify-between gap-3 flex-wrap">
                                <span className="text-sm font-medium text-slate-600">Investment</span>
                                <span className="text-xl sm:text-2xl font-bold text-[#0F4C81] whitespace-nowrap">${price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                              </div>
                              {c.show_cash_discount !== false && price > 0 && (
                                <div className="flex items-center justify-between gap-3 flex-wrap mt-1">
                                  <span className="text-xs text-emerald-600">Cash/Check Price (2% off)</span>
                                  <span className="text-base sm:text-lg font-semibold text-emerald-600 whitespace-nowrap">${(price * 0.98).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                                </div>
                              )}
                              <div className="mt-3 pt-3 border-t border-slate-200 flex flex-wrap gap-3 items-end">
                                <div className="flex-1 min-w-[100px]">
                                  <label className="text-[10px] text-slate-400 uppercase tracking-wide">Markup %</label>
                                  <input type="number" step="1" value={c.price_markup_pct || ""} onChange={(e) => updatePageContent(page.id, { ...c, price_markup_pct: e.target.value === "" ? 0 : parseFloat(e.target.value) })} placeholder="0" className="w-full mt-1 h-8 rounded border border-slate-200 px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#0F4C81]/30" data-testid={`option-markup-pct-${page.id}`} />
                                </div>
                                <div className="flex-1 min-w-[100px]">
                                  <label className="text-[10px] text-slate-400 uppercase tracking-wide">$ Adjust</label>
                                  <input type="number" step="50" value={c.price_adjustment || ""} onChange={(e) => updatePageContent(page.id, { ...c, price_adjustment: e.target.value === "" ? 0 : parseFloat(e.target.value) })} placeholder="0" className="w-full mt-1 h-8 rounded border border-slate-200 px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#0F4C81]/30" data-testid={`option-adjustment-${page.id}`} />
                                </div>
                              </div>
                              {/* Linked-option pricing — this option's total tracks another option's total + offset. */}
                              {(() => {
                                const otherOptions = (quote.pages || []).filter(p => p.page_type === "option" && p.id !== page.id && p.enabled !== false);
                                if (otherOptions.length === 0) return null;
                                const linkedId = c.linked_to_option_id || "";
                                const linkedTarget = otherOptions.find(p => p.id === linkedId);
                                const linkedActive = !!linkedId && !c.linked_to_option_disabled;
                                return (
                                  <div className="mt-3 pt-3 border-t border-dashed border-[#0F4C81]/20 space-y-2" data-testid={`option-link-config-${page.id}`}>
                                    <div className="flex items-center justify-between gap-2">
                                      <label className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">Link Price To Another Option</label>
                                      {linkedId && (
                                        <label className="flex items-center gap-1.5 text-[10px] text-slate-500 cursor-pointer">
                                          <input
                                            type="checkbox"
                                            checked={!c.linked_to_option_disabled}
                                            onChange={(e) => updatePageContent(page.id, { ...c, linked_to_option_disabled: !e.target.checked })}
                                            className="h-3 w-3"
                                            data-testid={`option-link-toggle-${page.id}`}
                                          />
                                          <span>Link active</span>
                                        </label>
                                      )}
                                    </div>
                                    <div className="flex flex-wrap gap-2 items-end">
                                      <div className="flex-1 min-w-[160px]">
                                        <label className="text-[10px] text-slate-400 uppercase tracking-wide">Target Option</label>
                                        <select
                                          value={linkedId}
                                          onChange={(e) => updatePageContent(page.id, { ...c, linked_to_option_id: e.target.value })}
                                          className="w-full mt-1 h-8 rounded border border-slate-200 px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#0F4C81]/30 bg-white"
                                          data-testid={`option-link-target-${page.id}`}
                                        >
                                          <option value="">— None (independent) —</option>
                                          {otherOptions.map(op => (
                                            <option key={op.id} value={op.id}>
                                              {(op.content?.option_name) || op.title || "Untitled option"}
                                            </option>
                                          ))}
                                        </select>
                                      </div>
                                      <div className="w-[110px]">
                                        <label className="text-[10px] text-slate-400 uppercase tracking-wide">Offset $</label>
                                        <input
                                          type="number"
                                          step="1"
                                          value={c.linked_offset ?? ""}
                                          onChange={(e) => updatePageContent(page.id, { ...c, linked_offset: e.target.value === "" ? "" : parseFloat(e.target.value) })}
                                          placeholder="-100"
                                          disabled={!linkedId}
                                          className="w-full mt-1 h-8 rounded border border-slate-200 px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#0F4C81]/30 disabled:bg-slate-50 disabled:text-slate-400"
                                          title="Positive to add, negative to subtract. e.g. -100 means this option is always $100 less than the target."
                                          data-testid={`option-link-offset-${page.id}`}
                                        />
                                      </div>
                                    </div>
                                    {linkedActive && linkedTarget && (
                                      <p className="text-[10px] text-[#0F4C81] bg-[#0F4C81]/5 border border-[#0F4C81]/15 rounded px-2 py-1.5">
                                        Price locked to <b>{(linkedTarget.content?.option_name) || linkedTarget.title || "target"}</b>
                                        {Number.isFinite(parseFloat(c.linked_offset)) && ` ${parseFloat(c.linked_offset) >= 0 ? "+" : "−"} $${Math.abs(parseFloat(c.linked_offset)).toLocaleString()}`}.
                                        {" "}Sells For / Markup / $ Adjust above are ignored while the link is active.
                                      </p>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          ) : null;
                        })()}
                        {c.show_warranty !== false && (c.warranty_material || c.warranty_wind) && (
                          <div className="mt-2 text-sm text-slate-500">
                            {c.warranty_material && <p>Material Warranty: {c.warranty_material}</p>}
                            {c.warranty_wind && <p>Wind Warranty: {c.warranty_wind}</p>}
                          </div>
                        )}
                        </div>
                      </div>
                    );
                    if (pt === "addon") return (
                      <div key={page.id} className="mt-6 border-2 border-emerald-200 rounded-xl overflow-hidden">
                        <div className="bg-emerald-600 px-5 py-2">
                          <span className="text-[10px] font-bold uppercase tracking-widest text-white/70">Add-On</span>
                        </div>
                        <div className="p-5 bg-white">
                          <h3 className="text-lg font-bold text-slate-700 mb-1">{c.addon_name || page.title}</h3>
                        {c.description && <RichTextContent content={c.description} className="text-slate-600 mb-2" />}
                        {/* Show total price only — no material breakdown */}
                        {c.show_price !== false && (() => {
                          // Match the same pricing logic used by Pages, Pricing, and
                          // the public customer view: prefer customer_price over unit_price,
                          // and honor base_price / manual_price overrides.
                          const manualBase = parseFloat(c.base_price || c.manual_price) || 0;
                          const items = (c.line_items || []).filter(it => it.visible !== false);
                          const itemsSum = items.reduce((sum, it) => {
                            const q = parseFloat(it.quantity) || 0;
                            const pr = parseFloat(it.customer_price || it.unit_price) || 0;
                            const m = parseFloat(it.margin) || 0;
                            const w = parseFloat(it.waste_factor) || 0;
                            return sum + q * (1 + w / 100) * pr * (1 + m / 100);
                          }, 0);
                          const taxRate = parseFloat(c.tax_rate) || 0;
                          const sub = manualBase > 0 ? manualBase : itemsSum;
                          const tot = manualBase > 0 ? manualBase : sub + sub * (taxRate / 100);
                          const bp = parseFloat(c.price);
                          const rawPrice = tot > 0 ? tot : (bp || 0);
                          const addonMarkupPct = parseFloat(c.price_markup_pct) || 0;
                          const addonAdjustment = parseFloat(c.price_adjustment) || 0;
                          const price = rawPrice + (rawPrice * addonMarkupPct / 100) + addonAdjustment;
                          return price > 0 ? (
                            <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-emerald-700">Add-on Price</span>
                                <span className="text-xl font-bold text-emerald-700">+ ${price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                              </div>
                              <div className="mt-2 pt-2 border-t border-emerald-200/50 flex flex-wrap gap-3 items-end">
                                <div className="flex-1 min-w-[100px]">
                                  <label className="text-[10px] text-emerald-600/60 uppercase tracking-wide">Markup %</label>
                                  <input
                                    type="number"
                                    step="1"
                                    value={c.price_markup_pct || ""}
                                    onChange={(e) => {
                                      const val = e.target.value === "" ? 0 : parseFloat(e.target.value);
                                      updatePageContent(page.id, { ...c, price_markup_pct: val });
                                    }}
                                    placeholder="0"
                                    className="w-full mt-1 h-8 rounded border border-emerald-200 px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-300"
                                    data-testid={`addon-markup-pct-${page.id}`}
                                  />
                                </div>
                                <div className="flex-1 min-w-[100px]">
                                  <label className="text-[10px] text-emerald-600/60 uppercase tracking-wide">$ Adjust</label>
                                  <input
                                    type="number"
                                    step="50"
                                    value={c.price_adjustment || ""}
                                    onChange={(e) => {
                                      const val = e.target.value === "" ? 0 : parseFloat(e.target.value);
                                      updatePageContent(page.id, { ...c, price_adjustment: val });
                                    }}
                                    placeholder="0"
                                    className="w-full mt-1 h-8 rounded border border-emerald-200 px-2 text-sm text-slate-700 focus:outline-none focus:ring-1 focus:ring-emerald-300"
                                    data-testid={`addon-adjustment-${page.id}`}
                                  />
                                </div>
                              </div>
                            </div>
                          ) : null;
                        })()}
                        </div>
                      </div>
                    );
                    if (pt === "custom") return (
                      <div key={page.id} className="mt-8 pt-6 border-t-2 border-slate-200">
                        {c.title && <h3 className="text-lg font-semibold text-slate-700 mb-3">{c.title}</h3>}
                        {(c.blocks || []).map((blk, bi) => {
                          if (blk.type === "text") { const txt = typeof blk.content === "string" ? blk.content : (blk.content?.html || ""); return txt ? <p key={bi} className="text-slate-600 text-base whitespace-pre-wrap mb-3 leading-relaxed">{txt}</p> : null; }
                          if (blk.type === "richtext") return <RichTextContent key={bi} content={blk.content} className="text-slate-600 text-base mb-3 leading-relaxed" />;
                          if (blk.type === "html") {
                            let html = typeof blk.content === "string" ? blk.content : (blk.content?.html || "");
                            html = html.replace(/https?:\/\/[^"'\s]+?(?=\/api\/uploads\/)/g, window.location.origin);
                            return <div key={bi} className="mb-3" dangerouslySetInnerHTML={{ __html: html }} />;
                          }
                          if (blk.type === "iframe" && blk.url) return (
                            <div key={bi} className="rounded-lg overflow-hidden border border-slate-200 mb-3">
                              <iframe src={blk.url} title={blk.title || "Embedded Content"} width="100%" height={blk.height || 700} frameBorder="0" className="w-full" style={{ minHeight: blk.height || 700 }} />
                            </div>
                          );
                          if (blk.type === "image") {
                            const src = resolveImageUrl(blk.url);
                            if (!src) return null;
                            const align = blk.textAlign || "center";
                            const w = blk.width || "full";
                            const widthStyle = w === "full" ? "100%" : `${w}%`;
                            const wrapClass = align === "left" ? "flex justify-start" : align === "right" ? "flex justify-end" : "flex justify-center";
                            return (
                              <div key={bi} className={`${wrapClass} mb-3`}>
                                <img src={src} alt={blk.alt || ""} className="rounded-lg object-contain" style={{ width: widthStyle, height: "auto", maxHeight: blk.height ? `${blk.height}px` : "none" }} />
                              </div>
                            );
                          }
                          if (blk.type === "video" && blk.url) {
                            const vUrl = resolveImageUrl(blk.url);
                            let vEmbed = "";
                            if (vUrl.includes("youtube.com/watch")) vEmbed = vUrl.replace("watch?v=", "embed/");
                            else if (vUrl.includes("youtu.be/")) vEmbed = `https://www.youtube.com/embed/${vUrl.split("youtu.be/")[1]}`;
                            else if (/vimeo\.com\/\d+/.test(vUrl)) vEmbed = `https://player.vimeo.com/video/${vUrl.split("vimeo.com/")[1].split(/[?#]/)[0]}`;
                            const vDirect = !vEmbed && (/\.(mp4|mov|webm|m4v|ogv)(\?|$)/i.test(vUrl) || vUrl.includes("/api/uploads/"));
                            return (
                              <div key={bi} className="mb-3 space-y-1">
                                {vDirect ? (
                                  <video controls playsInline src={vUrl} className="w-full rounded-lg bg-black border" preload="metadata" />
                                ) : (
                                  <div className="aspect-video rounded-lg overflow-hidden bg-slate-100"><iframe src={vEmbed || vUrl} title="Video" className="w-full h-full border-0" allowFullScreen /></div>
                                )}
                                {blk.caption && <p className="text-xs text-slate-500 text-center">{blk.caption}</p>}
                              </div>
                            );
                          }
                          if (blk.type === "before_after") {
                            return <div key={bi} className="mb-3"><BeforeAfterDisplay block={blk} /></div>;
                          }
                          if (blk.type === "columns") {
                            const cc = blk.columns || 2;
                            const cd = blk.column_data || [];
                            return (
                              <div key={bi} className={`grid gap-4 mb-3 ${cc === 2 ? "grid-cols-2" : cc === 3 ? "grid-cols-3" : "grid-cols-4"}`}>
                                {cd.slice(0, cc).map((col, ci) => (
                                  <div key={ci} className="text-sm text-slate-600">
                                    {col.type === "richtext" || (col.content && typeof col.content === "string" && col.content.startsWith("<")) ? <RichTextContent content={col.content} /> : typeof col.content === "string" ? <span className="whitespace-pre-wrap">{col.content}</span> : <RichTextContent content={col.content} />}
                                  </div>
                                ))}
                              </div>
                            );
                          }
                          if (blk.type === "table" && typeof blk.content === "object" && blk.content?.headers) {
                            return (
                              <table key={bi} className="w-full border border-slate-200 mb-2 text-sm">
                                <thead><tr className="bg-[#0F4C81] text-white">{blk.content.headers.map((h, hi) => <th key={hi} className="px-3 py-2 text-left">{h}</th>)}</tr></thead>
                                <tbody>{(blk.content.rows || []).map((row, ri) => <tr key={ri} className="border-b border-slate-100">{row.map((cell, ci) => <td key={ci} className="px-3 py-2">{cell}</td>)}</tr>)}</tbody>
                              </table>
                            );
                          }
                          return null;
                        })}
                      </div>
                    );
                    if (pt === "financing") return (
                      <div key={page.id} className="mt-8 pt-6 border-t-2 border-slate-200">
                        <h3 className="text-xl font-bold text-slate-800 mb-2">{c.title || "Financing Options"}</h3>
                        {c.intro && <p className="text-sm text-slate-600 mb-4">{c.intro}</p>}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <div className="border-2 border-emerald-200 rounded-xl overflow-hidden">
                            <div className="bg-emerald-600 px-4 py-3"><p className="text-white font-bold">HFS Financial</p><p className="text-emerald-100 text-xs">Funded Directly to You</p></div>
                            <div className="p-4"><p className="text-xs text-slate-600">{c.hfs_desc || "Funds deposited directly to the homeowner."}</p>
                              {c.hfs_link && <a href={c.hfs_link} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 px-4 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg">Get Pre-Qualified</a>}
                            </div>
                          </div>
                          <div className="border-2 border-blue-200 rounded-xl overflow-hidden">
                            <div className="bg-[#0F4C81] px-4 py-3"><p className="text-white font-bold">Wisetack</p><p className="text-blue-200 text-xs">Funded Directly to Contractor</p></div>
                            <div className="p-4"><p className="text-xs text-slate-600">{c.wisetack_desc || "Funds go directly to your contractor."}</p>
                              {c.wisetack_link && <a href={c.wisetack_link} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 px-4 py-2 bg-[#0F4C81] text-white text-xs font-bold rounded-lg">Get Pre-Qualified</a>}
                            </div>
                          </div>
                        </div>
                        {c.show_calculator !== false && (
                          <div className="border border-slate-200 rounded-lg overflow-hidden">
                            <iframe src="https://wisetack.us/#/payment_calculator?type=HS" title="Calculator" width="100%" height="400" frameBorder="0" className="w-full" />
                          </div>
                        )}
                      </div>
                    );
                    if (pt === "signature") return (
                      <div key={page.id} className="border-t border-slate-200 pt-6">
                        <h3 className="text-lg font-semibold text-slate-700 mb-4">Acceptance</h3>
                        <p className="text-slate-600 mb-6 text-sm">By signing below, you accept the terms and pricing of this proposal.</p>
                        <div className="space-y-6">
                          <div className="border-b border-slate-300 pb-2"><p className="text-xs text-slate-400">{c.customer_label || "Customer Signature"}</p></div>
                          <div className="border-b border-slate-300 pb-2"><p className="text-xs text-slate-400">{c.admin_label || "Company Signature"}</p></div>
                        </div>
                      </div>
                    );
                    return null;
                  })}
                  {/* Materials/line items hidden from client preview - totals shown per option/addon above */}
                  {/* Quote-level Discounts preview — mirrors customer View & Sign bottom summary.
                      Shown only when at least one discount has a positive value, so it stays
                      out of the way when nothing's configured. */}
                  {(() => {
                    const discounts = Array.isArray(quote.discounts) ? quote.discounts.filter(d => parseFloat(d?.value) > 0) : [];
                    const hasLegacy = !discounts.length && parseFloat(quote.discount_amount) > 0;
                    if (!discounts.length && !hasLegacy) return null;
                    // Use the FIRST enabled option as illustrative subtotal (customer picks one)
                    const firstOpt = (quote.pages || []).find(p => p.page_type === "option" && p.enabled !== false);
                    const optTotal = firstOpt ? calcFullOptionPrice(firstOpt) : 0;
                    const { totalRemoved, rows } = computeQuoteDiscounts(optTotal, quote);
                    const after = Math.max(0, optTotal - totalRemoved);
                    return (
                      <div className="mt-4 rounded-xl border-2 border-rose-200 bg-rose-50/40 p-4" data-testid="preview-discount-summary">
                        <p className="text-[10px] uppercase tracking-widest text-rose-700 font-bold mb-2">Quote Discounts (preview against first option)</p>
                        <div className="flex justify-between text-sm text-slate-600 mb-1">
                          <span>{firstOpt?.content?.option_name || "First option"} subtotal</span>
                          <span className="font-medium">${optTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                        </div>
                        {rows.map((d, i) => (
                          <div key={i} className="flex justify-between text-sm text-rose-700">
                            <span>{d.label}</span>
                            <span className="font-semibold">− ${d.removed.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                          </div>
                        ))}
                        <div className="flex justify-between text-base font-bold text-[#0F4C81] border-t border-rose-200 mt-2 pt-2">
                          <span>Customer pays</span>
                          <span>${after.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                        </div>
                        <p className="text-[10px] text-rose-700/70 mt-2 italic">Discounts apply once to whichever option the customer selects, plus any add-ons — exactly as shown on View &amp; Sign and the PDF.</p>
                      </div>
                    );
                  })()}
                  {quote.valid_until && <p className="text-sm text-slate-500 text-center pt-4">Valid until {new Date(quote.valid_until).toLocaleDateString()}</p>}
                </div>
              </CardContent>
            </Card>
            </SectionErrorBoundary>
          </TabsContent>
        </Tabs>
      </div>

      {/* =================== MODALS =================== */}

      {/* Add Page Modal */}
      <Dialog open={showPageModal} onOpenChange={(v) => { setShowPageModal(v); if (!v) { setImportSourceTplId(""); setImportSearchTerm(""); setNewPageType(""); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Page</DialogTitle>
            <DialogDescription>Start blank, or import an option / add-on you already built on another template.</DialogDescription>
          </DialogHeader>

          {/* -------- Import from another template -------- */}
          <div className="mt-3 border-2 border-dashed border-[#0F4C81]/30 rounded-lg p-3 bg-[#0F4C81]/5 space-y-2">
            <div className="flex items-center gap-2">
              <Copy className="w-4 h-4 text-[#0F4C81]" />
              <p className="text-sm font-semibold text-[#0F4C81]">Import Option / Add-on from another template</p>
            </div>
            <p className="text-[11px] text-slate-500">Great for reusing pre-built pages like Standing Seam, Full Coating, IKO Dynasty, etc. — the copy is fully editable inside this quote and won&apos;t affect the source template.</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Select value={importSourceTplId} onValueChange={setImportSourceTplId}>
                <SelectTrigger className="h-9 text-sm" data-testid="import-page-template-select">
                  <SelectValue placeholder="Pick a template to browse..." />
                </SelectTrigger>
                <SelectContent>
                  {(quoteTemplates || [])
                    .filter(t => t.id !== quote.template_id)
                    .map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name}{t.service_type ? ` — ${t.service_type}` : ""}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Input
                placeholder="Search by option / add-on name..."
                value={importSearchTerm}
                onChange={(e) => setImportSearchTerm(e.target.value)}
                className="h-9 text-sm"
                disabled={!importSourceTplId}
                data-testid="import-page-search"
              />
            </div>

            {importSourceTplId && (() => {
              const src = (quoteTemplates || []).find(t => t.id === importSourceTplId);
              const importable = (src?.pages || []).filter(p =>
                (p.page_type === "option" || p.page_type === "addon")
              );
              const term = importSearchTerm.trim().toLowerCase();
              const filtered = term
                ? importable.filter(p => {
                    const name = (p.content?.option_name || p.content?.addon_name || p.title || "").toLowerCase();
                    return name.includes(term);
                  })
                : importable;
              if (importable.length === 0) {
                return <p className="text-xs text-slate-500 italic">This template has no Option or Add-on pages.</p>;
              }
              return (
                <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1" data-testid="import-page-list">
                  {filtered.map(p => {
                    const isOpt = p.page_type === "option";
                    const name = p.content?.option_name || p.content?.addon_name || p.title || (isOpt ? "Untitled option" : "Untitled add-on");
                    const price = p.content?.base_price || p.content?.price;
                    return (
                      <button
                        type="button"
                        key={p.id}
                        onClick={() => handleImportPageFromTemplate(p)}
                        className="w-full text-left flex items-center gap-3 p-2.5 rounded-md border border-slate-200 bg-white hover:border-[#0F4C81] hover:bg-[#0F4C81]/5 transition-colors"
                        data-testid={`import-page-${p.id}`}
                      >
                        <Badge className={isOpt ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}>
                          {isOpt ? "Option" : "Add-on"}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">{name}</p>
                          {p.content?.description && (
                            <p className="text-[11px] text-slate-500 truncate">
                              {(typeof p.content.description === "string"
                                ? p.content.description
                                : (p.content.description?.html || "")).replace(/<[^>]+>/g, "").slice(0, 90)}
                            </p>
                          )}
                        </div>
                        {price ? (
                          <span className="text-xs font-bold text-[#0F4C81] whitespace-nowrap">
                            ${Number(price).toLocaleString()}
                          </span>
                        ) : null}
                        <Plus className="w-4 h-4 text-slate-400" />
                      </button>
                    );
                  })}
                  {filtered.length === 0 && (
                    <p className="text-xs text-slate-400 italic text-center py-2">No pages match &ldquo;{importSearchTerm}&rdquo;</p>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="flex items-center gap-3 my-2">
            <div className="flex-1 border-t border-slate-200" />
            <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">or start blank</span>
            <div className="flex-1 border-t border-slate-200" />
          </div>

          <div className="space-y-2">
            {PAGE_TYPES.map((pt) => {
              const Icon = pt.icon;
              return (
                <div key={pt.value}
                  className={`flex items-center gap-4 p-3 rounded-lg border-2 cursor-pointer transition-colors ${newPageType === pt.value ? "border-[#0F4C81] bg-[#0F4C81]/5" : "border-slate-200 hover:border-slate-300"}`}
                  onClick={() => setNewPageType(pt.value)}
                >
                  <Icon className="w-5 h-5 text-slate-600" />
                  <div><p className="font-medium text-sm text-slate-700">{pt.label}</p><p className="text-xs text-slate-500">{pt.description}</p></div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowPageModal(false)}>Cancel</Button>
            <Button className="flex-1 bg-[#0F4C81] hover:bg-[#0D4270]" onClick={handleAddPage} disabled={!newPageType}>Add Blank Page</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Line Item Modal */}
      <Dialog open={showLineItemModal} onOpenChange={setShowLineItemModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add {newLineItem.is_addon ? "Add-on" : "Line Item"}</DialogTitle>
            <DialogDescription>{newLineItem.is_addon ? "Optional item the customer can select" : "Select from price list or add custom"}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>From Price List</Label>
              <Select value={newLineItem.price_list_item_id || "none"} onValueChange={(v) => v !== "none" && handlePriceListSelect(v)}>
                <SelectTrigger className="h-12"><SelectValue placeholder="Select item" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Custom item...</SelectItem>
                  {priceList.map(item => <SelectItem key={item.id} value={item.id}>{item.name} - ${item.base_price}/{item.unit}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={newLineItem.name} onChange={(e) => setNewLineItem(p => ({ ...p, name: e.target.value }))} className="h-12" data-testid="line-item-name" />
              </div>
              <div className="space-y-2">
                <Label>Unit</Label>
                <Input value={newLineItem.unit} onChange={(e) => setNewLineItem(p => ({ ...p, unit: e.target.value }))} className="h-12" placeholder="sqft, lft, ea" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input value={newLineItem.description} onChange={(e) => setNewLineItem(p => ({ ...p, description: e.target.value }))} className="h-12" />
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Qty</Label>
                <Input type="number" value={newLineItem.quantity ?? ''} onChange={(e) => setNewLineItem(p => ({ ...p, quantity: e.target.value === '' ? '' : parseFloat(e.target.value) }))} className="h-12" />
              </div>
              <div className="space-y-2">
                <Label>Unit Price ($)</Label>
                <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span><Input type="number" step="0.01" value={newLineItem.unit_price ?? ''} onChange={(e) => setNewLineItem(p => ({ ...p, unit_price: e.target.value === '' ? '' : parseFloat(e.target.value) }))} className="h-12 pl-7" /></div>
              </div>
              <div className="space-y-2">
                <Label>Markup %</Label>
                <Input type="number" step="0.1" value={newLineItem.markup ?? ''} onChange={(e) => setNewLineItem(p => ({ ...p, markup: e.target.value === '' ? '' : parseFloat(e.target.value) }))} className="h-12" />
              </div>
              <div className="space-y-2">
                <Label>Discount ($)</Label>
                <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span><Input type="number" step="0.01" value={newLineItem.discount ?? ''} onChange={(e) => setNewLineItem(p => ({ ...p, discount: e.target.value === '' ? '' : parseFloat(e.target.value) }))} className="h-12 pl-7" /></div>
              </div>
            </div>
            {newLineItem.is_addon && (
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-700">
                This item will appear as an optional add-on on the signature page
              </div>
            )}
            <div className="p-3 bg-slate-50 rounded-lg text-sm text-slate-600">
              Preview: ${((newLineItem.quantity || 1) * (newLineItem.unit_price || 0) * (1 + (newLineItem.markup || 0) / 100) - (newLineItem.discount || 0)).toFixed(2)}
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowLineItemModal(false)}>Cancel</Button>
            <Button className="flex-1 bg-[#0F4C81] hover:bg-[#0D4270]" onClick={handleAddLineItem} disabled={!newLineItem.name} data-testid="confirm-add-item-btn">Add</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Modal */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-lg w-[95vw] max-h-[92vh] overflow-y-auto p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle>Send Quote via Email</DialogTitle>
            <DialogDescription>Sends a portal link. The customer downloads the PDF from that page.</DialogDescription>
          </DialogHeader>
          {loadingEmailTemplate ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#0F4C81]" /></div>
          ) : (
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Recipient Email *</Label>
                  {!showCcField && (
                    <button
                      type="button"
                      onClick={() => setShowCcField(true)}
                      className="text-xs font-medium text-[#0F4C81] hover:text-[#0D4270]"
                      data-testid="email-add-cc-btn"
                    >
                      + Add CC
                    </button>
                  )}
                </div>
                <Input type="email" value={emailRecipient} onChange={(e) => setEmailRecipient(e.target.value)} placeholder="customer@email.com" className="h-12" data-testid="email-recipient-input" />
              </div>
              {showCcField && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>CC (comma-separated)</Label>
                    <button
                      type="button"
                      onClick={() => { setEmailCcRecipients(""); setShowCcField(false); }}
                      className="text-xs text-slate-400 hover:text-slate-600"
                      data-testid="email-remove-cc-btn"
                    >
                      remove
                    </button>
                  </div>
                  <Input
                    type="text"
                    value={emailCcRecipients}
                    onChange={(e) => setEmailCcRecipients(e.target.value)}
                    placeholder="spouse@email.com, partner@email.com"
                    className="h-12"
                    data-testid="email-cc-input"
                  />
                  <p className="text-xs text-slate-400">Everyone gets the same email with the same portal link. All addresses are visible on the To: line.</p>
                </div>
              )}
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Subject line" className="h-12" data-testid="email-subject-input" />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Email Body</Label>
                  <VariableInsertButton onInsert={(v) => setEmailBody((emailBody || "") + v)} />
                </div>
                <Textarea
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  placeholder="Write your message to the client..."
                  rows={5}
                  className="text-sm"
                  data-testid="email-body-input"
                />
                <p className="text-xs text-slate-400">The email will include a "View Quote Online" button. Customers download the PDF from the portal page.</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-lg text-xs text-slate-500 space-y-1">
                <p>Quote #{quote.quote_number || "---"}</p>
                <p>Includes: online portal link (customer downloads PDF there)</p>
              </div>
              {spamScore && (
                <div className={`p-3 rounded-lg border ${spamScore.verdict === "inbox" ? "bg-emerald-50 border-emerald-200" : spamScore.verdict === "promotions" ? "bg-amber-50 border-amber-200" : "bg-rose-50 border-rose-200"}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm font-bold ${spamScore.verdict === "inbox" ? "text-emerald-700" : spamScore.verdict === "promotions" ? "text-amber-700" : "text-rose-700"}`}>{spamScore.verdict_label} · {spamScore.score}/10</span>
                    <button type="button" className="text-xs text-slate-400 hover:text-slate-600" onClick={() => setSpamScore(null)}>hide</button>
                  </div>
                  {spamScore.issues.length > 0 ? (
                    <ul className="text-xs text-slate-600 space-y-1 list-disc pl-4">
                      {spamScore.issues.map((i, idx) => (
                        <li key={idx} className={i.severity === "high" ? "text-rose-700" : i.severity === "medium" ? "text-amber-700" : "text-slate-500"}>{i.message}</li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-emerald-700">No spam triggers detected. Safe to send.</p>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Actions — Send is primary/full-width on mobile so it's always reachable */}
          <div className="mt-5 space-y-2 sm:space-y-0 sm:flex sm:gap-2 sm:flex-wrap">
            <Button className="w-full sm:flex-1 order-first sm:order-last h-12 bg-[#0F4C81] hover:bg-[#0D4270]" onClick={handleSendEmail} disabled={sendingEmail || !emailRecipient || loadingEmailTemplate} data-testid="send-email-confirm-btn">
              {sendingEmail ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}Send
            </Button>
            <Button variant="outline" className="w-full sm:w-auto h-11" onClick={handleSpamCheck} disabled={checkingSpam || !emailBody} data-testid="spam-check-btn">
              {checkingSpam ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1" />}Spam check
            </Button>
            <Button variant="outline" className="w-full sm:w-auto h-11" onClick={() => { setShowEmailModal(false); setPreviewDevice("mobile"); setShowCustomerPreview(true); }} data-testid="preview-before-send-btn">
              <Eye className="w-4 h-4 mr-1" />Preview
            </Button>
            <Button variant="ghost" className="w-full sm:w-auto h-11" onClick={() => setShowEmailModal(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Signature Modal - Type or Draw */}
      <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{signatureType === "customer" ? "Customer" : "Company"} Signature</DialogTitle>
            <DialogDescription>Sign by typing your name or drawing</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            {/* Method Toggle */}
            <div className="flex rounded-lg border border-slate-200 overflow-hidden">
              <button className={`flex-1 py-2 text-sm font-medium transition-colors ${signatureMethod === "type" ? "bg-[#0F4C81] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} onClick={() => setSignatureMethod("type")} data-testid="sig-method-type">Type Name</button>
              <button className={`flex-1 py-2 text-sm font-medium transition-colors ${signatureMethod === "draw" ? "bg-[#0F4C81] text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} onClick={() => setSignatureMethod("draw")} data-testid="sig-method-draw">Draw</button>
            </div>

            {signatureMethod === "type" ? (
              <div className="space-y-3">
                <div className="space-y-2">
                  <Label>Type your full name</Label>
                  <Input value={typedSignature} onChange={(e) => setTypedSignature(e.target.value)} placeholder="John Smith" className="h-12 text-lg" data-testid="type-signature-input" />
                </div>
                {typedSignature && (
                  <div className="border-2 border-slate-200 rounded-lg p-6 bg-white text-center">
                    <p className="text-3xl italic text-[#0F4C81]" style={{ fontFamily: "Georgia, serif" }}>{typedSignature}</p>
                    <div className="border-t border-slate-300 mt-4 pt-2">
                      <p className="text-xs text-slate-400">Digital Signature</p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <div className="border-2 border-slate-200 rounded-lg overflow-hidden">
                  <canvas ref={signatureCanvasRef} width={400} height={150} className="w-full cursor-crosshair bg-white" onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} />
                </div>
                <Button variant="outline" size="sm" className="w-full" onClick={clearSignature}>Clear</Button>
                <div className="space-y-2">
                  <Label>Printed Name</Label>
                  <Input value={typedSignature} onChange={(e) => setTypedSignature(e.target.value)} placeholder="Full name" className="h-12" />
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-3 mt-4">
            <Button variant="outline" className="flex-1" onClick={() => setShowSignatureModal(false)}>Cancel</Button>
            <Button
              className="flex-1 bg-[#0F4C81] hover:bg-[#0D4270]"
              onClick={() => handleSignQuote(typedSignature)}
              disabled={signatureMethod === "type" ? !typedSignature : false}
              data-testid="apply-signature-btn"
            >
              <PenTool className="w-4 h-4 mr-2" />Apply Signature
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Customer Preview Overlay */}
      {showCustomerPreview && (
        <div className="fixed inset-0 z-[60] bg-slate-900/80 backdrop-blur-sm flex flex-col" data-testid="customer-preview-overlay">
          {/* Preview Toolbar */}
          <div className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <Eye className="w-5 h-5 text-blue-400" />
              <span className="text-white font-semibold text-sm">View & Sign</span>
              <span className="text-slate-400 text-xs">— Present to customer and sign in person</span>
            </div>
            <div className="flex items-center gap-1 bg-slate-700 rounded-lg p-1" data-testid="device-toggle">
              {[
                { id: "mobile", icon: Smartphone, label: "Mobile", w: 390 },
                { id: "tablet", icon: Tablet, label: "Tablet", w: 768 },
                { id: "desktop", icon: Monitor, label: "Desktop", w: "100%" },
              ].map(d => (
                <button
                  key={d.id}
                  onClick={() => setPreviewDevice(d.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    previewDevice === d.id
                      ? "bg-[#0F4C81] text-white shadow-md"
                      : "text-slate-400 hover:text-white hover:bg-slate-600"
                  }`}
                  data-testid={`preview-${d.id}`}
                >
                  <d.icon className="w-4 h-4" />
                  {d.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-slate-300 hover:text-white hover:bg-slate-700 text-xs"
                onClick={() => window.open(`/public/quote/${id}`, '_blank')}
                data-testid="open-in-tab-btn"
              >
                <Link className="w-3.5 h-3.5 mr-1" />Open in Tab
              </Button>
              <button
                onClick={() => setShowCustomerPreview(false)}
                className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700 transition-colors"
                data-testid="close-preview-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Preview Content */}
          <div className="flex-1 flex items-start justify-center overflow-auto p-6">
            <div
              className="bg-white rounded-xl shadow-2xl overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                width: previewDevice === "mobile" ? 390 : previewDevice === "tablet" ? 768 : "100%",
                maxWidth: previewDevice === "desktop" ? 1200 : undefined,
                height: previewDevice === "desktop" ? "100%" : "auto",
              }}
            >
              {/* Device Frame for Mobile/Tablet */}
              {previewDevice !== "desktop" && (
                <div className="bg-slate-100 px-3 py-2 flex items-center gap-2 border-b border-slate-200">
                  <div className="flex gap-1">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 bg-white rounded-full px-3 py-0.5 text-[10px] text-slate-400 text-center truncate">
                    {window.location.origin}/public/quote/{id}
                  </div>
                </div>
              )}
              <iframe
                src={`/public/quote/${id}`}
                title="Customer Preview"
                className="w-full border-0"
                style={{
                  height: previewDevice === "desktop" ? "calc(100vh - 120px)" : "calc(100vh - 140px)",
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Undo Signing confirmation dialog */}
      <Dialog open={showUnsignConfirm} onOpenChange={setShowUnsignConfirm}>
        <DialogContent data-testid="unsign-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Undo signatures?
            </DialogTitle>
            <DialogDescription>
              This clears both signatures + all customer answers so you can edit the
              quote and re-send it for a fresh signature.
            </DialogDescription>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800 font-medium mb-1">What happens:</p>
              <ul className="text-xs text-amber-700 space-y-0.5 list-disc list-inside">
                <li>Both signatures are wiped (audit log kept)</li>
                <li>Status returns to <b>Sent</b></li>
                <li>Project rolls back to the <b>Quote Sent</b> stage</li>
                <li>If a deposit invoice was auto-created, you&apos;ll get a warning to void it</li>
              </ul>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowUnsignConfirm(false)}
              disabled={unsigning}
              data-testid="unsign-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700"
              onClick={handleUnsign}
              disabled={unsigning}
              data-testid="unsign-confirm-btn"
            >
              {unsigning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Yes, undo signing
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
